const installMediaQueryWatcher = (mediaQuery, changedCallback) => {
  const mq = window.matchMedia(mediaQuery);
  mq.addEventListener('change', e => changedCallback(e.matches));
  changedCallback(mq.matches);
};

const deferScriptLoad = (name, src, onload, requestVisualChange = false) => {
  window.Eurus.loadedScript.add(name);
  
  (events => {
    const loadScript = () => {
      events.forEach(type => window.removeEventListener(type, loadScript));
      clearTimeout(autoloadScript);

      const initScript = () => {
        const script = document.createElement('script');
        script.setAttribute('src', src);
        script.setAttribute('defer', '');
        script.onload = () => {
          document.dispatchEvent(new CustomEvent(name + ' loaded'));
          onload();
        };

        document.head.appendChild(script);
      }

      if (requestVisualChange) {
        if (window.requestIdleCallback) {
          requestIdleCallback(initScript);
        } else {
          requestAnimationFrame(initScript);
        }
      } else {
        initScript();
      }
    };

    let autoloadScript;
    if (Shopify.designMode) {
      loadScript();
    } else {
      const wait = window.innerWidth > 767 ? 2000 : 5000;
      events.forEach(type => window.addEventListener(type, loadScript, {once: true, passive: true}));
      autoloadScript = setTimeout(() => {
        loadScript();
      }, wait);
    }
  })(['touchstart', 'mouseover', 'wheel', 'scroll', 'keydown']);
}

const getSectionInnerHTML = (html, selector = '.shopify-section') => {
  return new DOMParser()
    .parseFromString(html, 'text/html')
    .querySelector(selector).innerHTML;
}

const xParseJSON = (jsonString) => {
  jsonString = String.raw`${jsonString}`;
  jsonString = jsonString.replaceAll("\\","\\\\").replaceAll('\\"', '\"');

  return JSON.parse(jsonString);
}

const createCardProductSliderController = () => {
  const selector = '[data-card-slider="true"]';
  const gridSelector = '#ProductGridContainer';
  const desktopMedia = window.matchMedia('(min-width: 1024px)');
  const stateMap = new WeakMap();
  const activeSliderRoots = new Set();
  let sliderObserver = null;
  let gridObserver = null;
  let observedGrid = null;
  let handlersAttached = false;
  let interactionHandlersAttached = false;
  let gridRescanPending = false;

  const isDesktop = () => desktopMedia.matches;
  const isRtl = () => document.body.classList.contains('rtl') || document.documentElement.getAttribute('dir') === 'rtl';
  const getSlides = (root) => Array.from(root.querySelectorAll('[data-card-slide]'));

  const stopAutoplay = (state) => {
    if (state.autoplayTimer) {
      clearInterval(state.autoplayTimer);
      state.autoplayTimer = null;
    }
  };

  const attachHoverListeners = (state) => {
    if (!state || state.mode !== 'autoplay-hover' || state.hoverListenersAttached) return;

    state.onMouseEnter = () => {
      if (!isDesktop()) return;
      startAutoplay(state);
    };
    state.onMouseLeave = () => {
      stopAutoplay(state);
    };

    state.root.addEventListener('mouseenter', state.onMouseEnter);
    state.root.addEventListener('mouseleave', state.onMouseLeave);
    state.hoverListenersAttached = true;
  };

  const detachHoverListeners = (state) => {
    if (!state || !state.hoverListenersAttached) return;

    if (state.onMouseEnter) {
      state.root.removeEventListener('mouseenter', state.onMouseEnter);
      state.onMouseEnter = null;
    }
    if (state.onMouseLeave) {
      state.root.removeEventListener('mouseleave', state.onMouseLeave);
      state.onMouseLeave = null;
    }
    state.hoverListenersAttached = false;
    stopAutoplay(state);
  };

  const ensureInteractionHandlers = () => {
    if (interactionHandlersAttached) return;
    interactionHandlersAttached = true;
    document.addEventListener('click', handleClick, true);
    document.addEventListener('keydown', handleKeydown);
  };

  const maybeDetachInteractionHandlers = () => {
    if (!interactionHandlersAttached) return;
    if (document.querySelector(selector)) return;
    interactionHandlersAttached = false;
    document.removeEventListener('click', handleClick, true);
    document.removeEventListener('keydown', handleKeydown);
  };

  const pruneDisconnectedSliders = () => {
    activeSliderRoots.forEach((root) => {
      if (root.isConnected) return;

      const state = stateMap.get(root);
      if (state) {
        detachHoverListeners(state);
        stateMap.delete(root);
      }

      activeSliderRoots.delete(root);
      if (sliderObserver) sliderObserver.unobserve(root);
    });

    maybeDetachInteractionHandlers();
  };

  const hydrateSlideImage = (slide) => {
    if (!slide) return;

    const img = slide.querySelector('img.card-carousel-deferred-img[data-src]');
    if (!img) return;

    if (img.dataset.src) img.src = img.dataset.src;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    delete img.dataset.src;
    delete img.dataset.srcset;
  };

  const updateA11y = (state) => {
    const isInteractive = isDesktop() && state.slides.length > 1;

    state.root.setAttribute('tabindex', isInteractive ? '0' : '-1');
    state.root.setAttribute('aria-live', state.mode === 'autoplay-hover' ? 'off' : 'polite');

    state.slides.forEach((slide, index) => {
      const isActive = index === state.index;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      slide.setAttribute('aria-current', isActive ? 'true' : 'false');
      slide.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    if (state.prevButton) state.prevButton.disabled = !isInteractive;
    if (state.nextButton) state.nextButton.disabled = !isInteractive;
  };

  const setSlide = (state, index, { animate = true } = {}) => {
    state.index = index;
    state.list.classList.toggle('card-product-slider__list--no-transition', !animate);
    state.list.style.transform = `translate3d(${state.index * -100}%, 0, 0)`;

    if (!animate) {
      requestAnimationFrame(() => {
        state.list.classList.remove('card-product-slider__list--no-transition');
      });
    }

    hydrateSlideImage(state.slides[state.index]);
    updateA11y(state);
  };

  const runWrapFade = (state, nextIndex) => {
    if (state.isWrapping) return;

    stopAutoplay(state);
    state.isWrapping = true;
    state.root.classList.add('card-product-slider--wrap-fade', 'card-product-slider--faded');

    window.setTimeout(() => {
      setSlide(state, nextIndex, { animate: false });

      requestAnimationFrame(() => {
        state.root.classList.remove('card-product-slider--faded');

        window.setTimeout(() => {
          state.root.classList.remove('card-product-slider--wrap-fade');
          state.isWrapping = false;

          if (state.mode === 'autoplay-hover' && state.root.matches(':hover') && isDesktop()) {
            startAutoplay(state);
          }
        }, state.fadeInMs);
      });
    }, state.fadeOutMs);
  };

  const moveTo = (state, nextIndex, isWrapTransition = false) => {
    if (!isDesktop() || state.slides.length < 2 || state.isWrapping || nextIndex === state.index) return;

    if (isWrapTransition && state.wrapFade) {
      runWrapFade(state, nextIndex);
      return;
    }

    setSlide(state, nextIndex, { animate: true });
  };

  const nextSlide = (state) => {
    const lastIndex = state.slides.length - 1;
    const isWrap = state.index === lastIndex;
    const nextIndex = isWrap ? 0 : state.index + 1;
    moveTo(state, nextIndex, isWrap);
  };

  const prevSlide = (state) => {
    const isWrap = state.index === 0;
    const prevIndex = isWrap ? state.slides.length - 1 : state.index - 1;
    moveTo(state, prevIndex, isWrap);
  };

  const startAutoplay = (state) => {
    if (state.mode !== 'autoplay-hover' || state.slides.length < 2 || !isDesktop() || state.autoplayTimer) return;

    state.autoplayTimer = window.setInterval(() => {
      if (!document.body.contains(state.root) || !isDesktop()) {
        stopAutoplay(state);
        return;
      }
      nextSlide(state);
    }, state.intervalMs);
  };

  const syncStateWithViewport = (state) => {
    if (!state) return;

    if (!isDesktop()) {
      stopAutoplay(state);
      setSlide(state, 0, { animate: false });
    } else {
      updateA11y(state);
      if (state.mode === 'autoplay-hover' && state.root.matches(':hover')) {
        startAutoplay(state);
      }
    }
  };

  const initSlider = (root) => {
    if (!root || stateMap.has(root)) return stateMap.get(root);

    const track = root.querySelector('[data-card-slider-track]');
    const list = root.querySelector('[data-card-slider-list]');
    const slides = getSlides(root);

    if (!track || !list || slides.length === 0) return null;

    const mode = root.dataset.cardSliderMode === 'autoplay-hover' ? 'autoplay-hover' : 'arrows';
    const intervalRaw = parseInt(root.dataset.cardSliderInterval || '2000', 10);
    const intervalMs = Number.isFinite(intervalRaw) ? Math.max(250, intervalRaw) : 2000;
    const transitionMs = mode === 'autoplay-hover' ? intervalMs : 400;
    const fadeOutRaw = parseInt(root.dataset.cardSliderWrapFadeOut || '120', 10);
    const fadeInRaw = parseInt(root.dataset.cardSliderWrapFadeIn || '120', 10);
    const fadeOutMs = Number.isFinite(fadeOutRaw) ? Math.max(0, fadeOutRaw) : 120;
    const fadeInMs = Number.isFinite(fadeInRaw) ? Math.max(0, fadeInRaw) : 120;

    root.style.setProperty('--card-slider-transition-ms', `${transitionMs}ms`);
    root.style.setProperty('--card-slider-fade-out-ms', `${fadeOutMs}ms`);
    root.style.setProperty('--card-slider-fade-in-ms', `${fadeInMs}ms`);

    const state = {
      root,
      list,
      slides,
      index: 0,
      mode,
      intervalMs,
      fadeOutMs,
      fadeInMs,
      wrapFade: root.dataset.cardSliderWrapFade !== 'false',
      autoplayTimer: null,
      isWrapping: false,
      hoverListenersAttached: false,
      onMouseEnter: null,
      onMouseLeave: null,
      prevButton: root.querySelector('[data-card-nav="prev"]'),
      nextButton: root.querySelector('[data-card-nav="next"]')
    };

    if (state.prevButton) state.prevButton.setAttribute('aria-controls', root.id);
    if (state.nextButton) state.nextButton.setAttribute('aria-controls', root.id);

    stateMap.set(root, state);
    activeSliderRoots.add(root);
    ensureInteractionHandlers();
    root.dataset.cardSliderInitialized = 'true';

    attachHoverListeners(state);
    setSlide(state, 0, { animate: false });
    syncStateWithViewport(state);

    return state;
  };

  const ensureSlider = (root) => stateMap.get(root) || initSlider(root);

  const observeSliders = (scope = document) => {
    pruneDisconnectedSliders();

    const roots = scope.matches && scope.matches(selector)
      ? [scope]
      : Array.from(scope.querySelectorAll(selector));

    if (!roots.length) return;
    ensureInteractionHandlers();

    if (!('IntersectionObserver' in window)) {
      roots.forEach(initSlider);
      return;
    }

    if (!sliderObserver) {
      sliderObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          sliderObserver.unobserve(entry.target);
          initSlider(entry.target);
        });
      }, { root: null, rootMargin: '200px 0px', threshold: 0 });
    }

    roots.forEach((root) => {
      if (root.dataset.cardSliderObserved === 'true' || root.dataset.cardSliderInitialized === 'true') return;
      root.dataset.cardSliderObserved = 'true';
      sliderObserver.observe(root);
    });
  };

  const nodeContainsSlider = (node) => {
    if (
      !node
      || (node.nodeType !== Node.ELEMENT_NODE && node.nodeType !== Node.DOCUMENT_FRAGMENT_NODE)
    ) {
      return false;
    }

    if (node.nodeType === Node.ELEMENT_NODE && node.matches && node.matches(selector)) {
      return true;
    }

    return !!(node.querySelector && node.querySelector(selector));
  };

  const hasRelevantGridMutations = (mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue;

      for (const node of mutation.addedNodes) {
        if (nodeContainsSlider(node)) return true;
      }

      for (const node of mutation.removedNodes) {
        if (nodeContainsSlider(node)) return true;
      }
    }

    return false;
  };

  const disconnectGridObserver = () => {
    if (gridObserver) {
      gridObserver.disconnect();
      gridObserver = null;
    }
    observedGrid = null;
  };

  const connectGridObserver = (grid) => {
    if (!grid) return;

    if (gridObserver) gridObserver.disconnect();

    observedGrid = grid;
    gridObserver = new MutationObserver((mutations) => {
      if (!hasRelevantGridMutations(mutations)) return;
      scheduleGridRescan();
    });

    gridObserver.observe(grid, { childList: true, subtree: true });
  };

  const runGridRescan = () => {
    gridRescanPending = false;
    pruneDisconnectedSliders();

    if (observedGrid && observedGrid.isConnected) {
      observeSliders(observedGrid);
    }

    const grid = document.querySelector(gridSelector);

    if (!grid) {
      disconnectGridObserver();
      return;
    }

    if (observedGrid !== grid) {
      connectGridObserver(grid);
      observeSliders(grid);
    }
  };

  const scheduleGridRescan = () => {
    if (gridRescanPending) return;
    gridRescanPending = true;

    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(runGridRescan);
    } else {
      window.setTimeout(runGridRescan, 16);
    }
  };

  const observeProductGridMutations = () => {
    const grid = document.querySelector(gridSelector);
    if (!grid) {
      disconnectGridObserver();
      return;
    }
    if (observedGrid === grid) return;
    connectGridObserver(grid);
  };

  const syncAllSliders = () => {
    pruneDisconnectedSliders();
    activeSliderRoots.forEach((root) => {
      const state = stateMap.get(root);
      if (state) syncStateWithViewport(state);
    });
  };

  const handleClick = (event) => {
    const button = event.target.closest('[data-card-nav]');
    if (!button) return;

    const root = button.closest(selector);
    if (!root) return;

    const state = ensureSlider(root);
    if (!state || !isDesktop() || state.slides.length < 2 || state.isWrapping) return;

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    if (button.dataset.cardNav === 'prev') {
      prevSlide(state);
    } else {
      nextSlide(state);
    }
  };

  const handleKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    const root = event.target.closest(selector);
    if (!root) return;

    const state = ensureSlider(root);
    if (!state || !isDesktop() || state.slides.length < 2 || state.isWrapping) return;

    event.preventDefault();
    const rtl = isRtl();

    if (event.key === 'ArrowRight') {
      rtl ? prevSlide(state) : nextSlide(state);
    } else {
      rtl ? nextSlide(state) : prevSlide(state);
    }
  };

  const attachHandlers = () => {
    if (handlersAttached) return;
    handlersAttached = true;

    document.addEventListener('eurus:product-grid:updated', () => {
      observeProductGridMutations();
      scheduleGridRescan();
    });
    desktopMedia.addEventListener('change', syncAllSliders);
    document.addEventListener('shopify:section:load', () => {
      observeSliders(document);
      observeProductGridMutations();
      scheduleGridRescan();
    });
    document.addEventListener('shopify:section:reorder', () => {
      observeSliders(document);
      observeProductGridMutations();
      scheduleGridRescan();
    });
  };

  const init = () => {
    attachHandlers();
    observeSliders(document);
    observeProductGridMutations();
    scheduleGridRescan();
  };

  return {
    init,
    refresh() {
      observeSliders(document);
      observeProductGridMutations();
      scheduleGridRescan();
    }
  };
};

const cardProductSliderController = createCardProductSliderController();
window.Eurus.cardProductSliderController = cardProductSliderController;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    cardProductSliderController.init();
  }, { once: true });
} else {
  cardProductSliderController.init();
}

const registerAlpineComponents = () => {
  Alpine.store('xDarkMode', {
      toggleThemeMode() {
        if (document.documentElement.classList.contains('dark')) {
          localStorage.eurus_theme = 0;
          document.documentElement.classList.remove('dark');
        } else {
          localStorage.eurus_theme = 1;
          document.documentElement.classList.add('dark');
        }
        Alpine.store('xHeaderMenu').setTopStickyHeader();
      }
    });

    Alpine.store('xHelper', {
      countdown(configs, callback) {
        let endDate = new Date(
          configs.end_year,
          configs.end_month - 1,
          configs.end_day,
          configs.end_hour,
          configs.end_minute
        );
        const endTime = endDate.getTime() + (-1 * configs.timezone * 60 - endDate.getTimezoneOffset()) * 60 * 1000;
        
        let startTime;
        if (configs.start_year) {
          let startDate = new Date(
            configs.start_year,
            configs.start_month - 1,
            configs.start_day,
            configs.start_hour,
            configs.start_minute
          );
          startTime = startDate.getTime() + (-1 * configs.timezone * 60 - startDate.getTimezoneOffset()) * 60 * 1000;
        } else {
          startTime = new Date().getTime();
        }

        let x = setInterval(function() {
          let now = new Date().getTime();
          let distance = endTime - now;

          if (distance < 0 || startTime > now) {
            callback(false, 0, 0, 0, 0);
            clearInterval(x);
          } else {
            var days = Math.floor(distance / (1000 * 60 * 60 * 24));
            var hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            var minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            var seconds = Math.floor((distance % (1000 * 60)) / 1000);
            minutes = minutes < 10 ? "0" + minutes : minutes;
            seconds = seconds < 10 ? "0" + seconds : seconds;

            callback(true, seconds, minutes, hours, days);
          }
        }, 1000);
      },
      canShow(configs) {
        let endDate = new Date(
          configs.end_year,
          configs.end_month - 1,
          configs.end_day,
          configs.end_hour,
          configs.end_minute
        );
        const endTime = endDate.getTime() + (-1 * configs.timezone * 60 - endDate.getTimezoneOffset()) * 60 * 1000;
        
        let startTime;
        if (configs.start_year) {
          let startDate = new Date(
            configs.start_year,
            configs.start_month - 1,
            configs.start_day,
            configs.start_hour,
            configs.start_minute
          );
          startTime = startDate.getTime() + (-1 * configs.timezone * 60 - startDate.getTimezoneOffset()) * 60 * 1000;
        } else {
          startTime = new Date().getTime();
        }
        let now = new Date().getTime();
        let distance = endTime - now;
        if (distance < 0 || startTime > now) {
          return false;
        } 
        return true;
      },
      handleTime(configs) {
        let endDate = new Date(
          configs.end_year,
          configs.end_month - 1,
          configs.end_day,
          configs.end_hour,
          configs.end_minute
        );
        const endTime = endDate.getTime() + (-1 * configs.timezone * 60 - endDate.getTimezoneOffset()) * 60 * 1000;
        
        let startTime;
        if (configs.start_year) {
          let startDate = new Date(
            configs.start_year,
            configs.start_month - 1,
            configs.start_day,
            configs.start_hour,
            configs.start_minute
          );
          startTime = startDate.getTime() + (-1 * configs.timezone * 60 - startDate.getTimezoneOffset()) * 60 * 1000;
        } else {
          startTime = new Date().getTime();
        }
        let now = new Date().getTime();
        let distance = endTime - now;
        return { "startTime": startTime, "endTime": endTime, "now": now, "distance": distance};
      }
    });

    Alpine.data('xCart', () => ({
      t: '',
      loading: false,
      updateItemQty(itemId, line) {
        let qty = parseInt(document.getElementById(`cart-qty-${itemId}`).value);
        if (this.validateQty(qty)) {
          this._postUpdateItem(itemId, line, qty);
        }
      },
      minusItemQty(itemId, line) {
        let qty = parseInt(document.getElementById(`cart-qty-${itemId}`).value);
        if (this.validateQty(qty)) {
          if (qty > 0) {
            qty -= 1;
            document.getElementById(`cart-qty-${itemId}`).value = qty;
          }

          this._postUpdateItem(itemId, line, qty);
        }
      },
      plusItemQty(itemId, line) {
        let qty = parseInt(document.getElementById(`cart-qty-${itemId}`).value);
        if (this.validateQty(qty)) {
          if (qty >= 0) {
            qty += 1;
            document.getElementById(`cart-qty-${itemId}`).value = qty;
          }

          this._postUpdateItem(itemId, line, qty);
        }
      },
      removeItem(itemId, line) {
        this._postUpdateItem(itemId, line, 0, 0);
      },
      removeItemHidePrice(itemId, line){
        clearTimeout(this.t);

        const func = () => {
          this.loading = true;

          const sections = Alpine.store('xCartHelper').getSectionsToRender().map(s => s.id);
          let updateData = {
            'line': `${line}`,
            'quantity': `0`,
            'sections': sections
          };

          fetch(`${Shopify.routes.root}cart/change.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updateData)
          })
            .then(response => response.text())
            .then(state => {
              const parsedState = JSON.parse(state);

              if (parsedState.status == '422') {
                this._addErrorMessage(itemId, parsedState.message);
                this.updateCart(line);
              } else {
                const items = document.querySelectorAll('.cart-item');
                if (parsedState.errors) {
                  this._addErrorMessage(itemId, parsedState.errors);
                  return;
                }

                const currentItemCount = Alpine.store('xCartHelper').currentItemCount
                Alpine.store('xCartHelper').currentItemCount = parsedState.item_count;
                if (currentItemCount != parsedState.item_count) {
                  document.dispatchEvent(new CustomEvent("eurus:cart:items-changed"));
                }

                const lineItemError = document.getElementById(`LineItemError-${itemId}`);
                if (lineItemError) {lineItemError.classList.add('hidden');}

                const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;


                if (items.length === parsedState.items.length && updatedValue != 0) {
                  let message = '';
                  if (typeof updatedValue === 'undefined') {
                    message = window.Eurus.cart_error;
                  } else {
                    message = window.Eurus.cart_quantity_error_html.replace('[quantity]', updatedValue);
                  }
                  this._addErrorMessage(itemId, message);
                }
              }
              let loadingEl = document.getElementById(`loading-${itemId}`);
              let removeEl = document.getElementById(`remove-${itemId}`);
              if (removeEl) {
                removeEl.style.display = 'block';
              }
              if (loadingEl) {
                loadingEl.classList.add('hidden');
              }
              this.loading = false;
            });
        }

        this.t = setTimeout(() => {
          func();
        }, 300);
      },
      removeBundle(itemId, listBundle) {
        const bundle = listBundle.split('||').filter(item => item.includes(itemId))[0];
        const items = [
          {
            id: bundle.split('__')[1].split('//')[0],
            line: parseInt(bundle.split('__')[1].split('//')[1])
          },
          {
            id: bundle.split('__')[2].split('//')[0],
            line: parseInt(bundle.split('__')[2].split('//')[1])
          }
        ]
        this.loading = true;
        let removeEl1 = document.getElementById(`remove-${items[0].id}`);
        let removeEl2 = document.getElementById(`remove-${items[1].id}`);
        if(removeEl1){
          removeEl1.style.display = 'none';
        }
        if(removeEl2){
          removeEl2.style.display = 'none';
        }
        document.getElementById(`loading-${items[0].id}`).classList.remove('hidden');
        document.getElementById(`loading-${items[1].id}`).classList.remove('hidden');

        const updateData = {
          updates: {
            [items[0].id]: 0,
            [items[1].id]: 0,
          },
          sections: Alpine.store('xCartHelper').getSectionsToRender().map(s => s.id)
        }
        fetch(`${Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        })
          .then(response => response.text())
          .then(state => {
            const parsedState = JSON.parse(state);
            if (parsedState.status == '422') {
              this._addErrorMessage(items[0].id, parsedState.message);
              this.updateCart(items[0].line);
              this._addErrorMessage(items[1].id, parsedState.message);
              this.updateCart(items[1].line);
            } else {
              const cartItemEls = document.querySelectorAll('.cart-item');
              if (parsedState.errors) {
                this._addErrorMessage(items[0].id, parsedState.message);
                this._addErrorMessage(items[1].id, parsedState.message);
                return;
              }
              Alpine.store('xCartHelper').getSectionsToRender().forEach((section => {
                const sectionElement = document.querySelector(section.selector);
                if (sectionElement) {
                  if (parsedState.sections[section.id])
                    sectionElement.innerHTML = getSectionInnerHTML(parsedState.sections[section.id], section.selector);
                }
              }));

              const currentItemCount = Alpine.store('xCartHelper').currentItemCount
              Alpine.store('xCartHelper').currentItemCount = parsedState.item_count;
              if (currentItemCount != parsedState.item_count) {
                document.dispatchEvent(new CustomEvent("eurus:cart:items-changed"));
              }

              const lineItemError1 = document.getElementById(`LineItemError-${items[0].id}`);
              const lineItemError2 = document.getElementById(`LineItemError-${items[1].id}`);
              if (lineItemError1) {lineItemError1.classList.add('hidden');}
              if (lineItemError2) {lineItemError2.classList.add('hidden');}

              const updatedValue1 = parsedState.items[items[0].line - 1] ? parsedState.items[items[0].line - 1].quantity : undefined;
              const updatedValue2 = parsedState.items[items[1].line - 1] ? parsedState.items[items[1].line - 1].quantity : undefined;

              if (cartItemEls.length === parsedState.items.length && updatedValue1 !== 0 && updatedValue2 !== 0) {
                let message = '';
                if (typeof updatedValue1 === 'undefined' || typeof updatedValue2 === 'undefined') {
                  message = window.Eurus.cart_error;
                } else {
                  message = window.Eurus.cart_quantity_error_html.replace('[quantity]', updatedValue1);
                }
                this._addErrorMessage(items[0].id, message);
                this._addErrorMessage(items[1].id, message);
              }
            }
            const loadingEl1 = document.getElementById(`loading-${items[0].id}`);
            const removeEl1 = document.getElementById(`remove-${items[0].id}`);
            const loadingEl2 = document.getElementById(`loading-${items[1].id}`);
            const removeEl2 = document.getElementById(`remove-${items[1].id}`);
            if (removeEl1) {
              removeEl1.style.display = 'block';
            }
            if (loadingEl1) {
              loadingEl1.classList.add('hidden');
            }
            if (removeEl2) {
              removeEl2.style.display = 'block';
            }
            if (loadingEl2) {
              loadingEl2.classList.add('hidden');
            }
            this.loading = false;
          });
      },
      handleKeydown(evt, el) {
        if (evt.key !== 'Enter') return;
        evt.preventDefault();
        el.blur();
        el.focus();
      },
      _postUpdateItem(itemId, line, qty, wait = 500) {
        clearTimeout(this.t);

        const func = () => {
          this.loading = true;
          let removeEl = document.getElementById(`remove-${itemId}`);
          if(removeEl){
            removeEl.style.display = 'none';
          }
          document.getElementById(`loading-${itemId}`).classList.remove('hidden');
          const sections = Alpine.store('xCartHelper').getSectionsToRender().map(s => s.id);
          let updateData = {
            'line': `${line}`,
            'quantity': `${qty}`,
            'sections': sections
          };

          fetch(`${Shopify.routes.root}cart/change.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updateData)
          })
            .then(response => response.text())
            .then(state => {
              const parsedState = JSON.parse(state);
              
              if (parsedState.status == '422') {
                this._addErrorMessage(itemId, parsedState.message);
                this.updateCart(line);
              } else {
                const items = document.querySelectorAll('.cart-item');
                if (parsedState.errors) {
                  this._addErrorMessage(itemId, parsedState.errors);
                  return;
                }
                Alpine.store('xCartHelper').getSectionsToRender().forEach((section => {
                  const sectionElement = document.querySelector(section.selector);
                  if (sectionElement) {
                    if (parsedState.sections[section.id])
                      sectionElement.innerHTML = getSectionInnerHTML(parsedState.sections[section.id], section.selector);
                  }
                }));

                const currentItemCount = Alpine.store('xCartHelper').currentItemCount
                Alpine.store('xCartHelper').currentItemCount = parsedState.item_count;
                if (currentItemCount != parsedState.item_count) {
                  document.dispatchEvent(new CustomEvent("eurus:cart:items-changed"));
                }

                const lineItemError = document.getElementById(`LineItemError-${itemId}`);
                if (lineItemError) {lineItemError.classList.add('hidden');}
                
                const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
                
                
                if (items.length === parsedState.items.length && updatedValue !== parseInt(qty)) {
                  let message = '';
                  if (typeof updatedValue === 'undefined') {
                    message = window.Eurus.cart_error;
                  } else {
                    message = window.Eurus.cart_quantity_error_html.replace('[quantity]', updatedValue);
                  }
                  this._addErrorMessage(itemId, message);
                }
              }
              let loadingEl = document.getElementById(`loading-${itemId}`);
              let removeEl = document.getElementById(`remove-${itemId}`);
              if(removeEl){
                removeEl.style.display = 'block';
              }
              if (loadingEl) {
                loadingEl.classList.add('hidden');
              }
              this.loading = false;
            });
        }

        this.t = setTimeout(() => {
          func();
        }, wait);
      },
      updateCart(line) {
        const sections = Alpine.store('xCartHelper').getSectionsToRender().map(s => s.id).join(',');
        fetch(
          `${window.location.pathname}?sections=${sections}`
        )
        .then(response => response.json())
        .then(sectionData => {
          Alpine.store('xCartHelper').getSectionsToRender().forEach(section => {
            if (!sectionData[section.id]) return;
            const sectionElement = document.querySelector(section.selector);
            if (sectionElement) {
              sectionElement.innerHTML = getSectionInnerHTML(sectionData[section.id], section.selector);
            }
          });
        });
      },
      updateEstimateShipping(el, line, itemId) {
        let qty = parseInt(document.getElementById(`cart-qty-${itemId}`).value);
        let properties = xParseJSON(el.getAttribute("x-data-properties"));
        let updateData = {
          'line': `${line}`,
          'quantity': `${qty}`,
          'properties': properties
        };
        fetch(`${Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        })
        .then(response => response.text());
      },
      updateDate(date) {
        var formData = {
          'attributes': {
            'datetime-updated': `${date}`           
          }
        }; 
        fetch(Shopify.routes.root+'cart/update', {
          method:'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(formData)
        })
      },
      _addErrorMessage(itemId, message) {
        const lineItemError = document.getElementById(`LineItemError-${itemId}`);
        if (!lineItemError) return;
        lineItemError.classList.remove('hidden');
        lineItemError
          .getElementsByClassName('cart-item__error-text')[0]
          .innerHTML = message;
      },
      validateQty: function(number) {
        if((parseFloat(number) != parseInt(number)) && isNaN(number)) {
          return false
        }

        return true;
      }
    }));

    Alpine.store('xCartHelper', {
      currentItemCount: 0,
      validated: true,
      openField: '',
      updateCart: function(data, needValidate = false) {
        const formData = JSON.stringify(data);
        fetch(Shopify.routes.root + 'cart/update', {
          method:'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: formData
        }).then(() => {
          if (needValidate) this.validateCart();
        });
      },
      validateCart: function() {
        this.validated = true;

        document.dispatchEvent(new CustomEvent("eurus:cart:validate"));
      },
      goToCheckout(e) {
        this.validateCart();
        
        if (this.validated) {
          let formData = {
            'attributes': {
              'collection-pagination': null,
              'blog-pagination': null,
              'choose_option_id': null,
              'datetime-updated': null
            }
          };

          fetch(Shopify.routes.root+'cart/update', {
            method:'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(formData)
          });
        } else {
          e.preventDefault();
        }
      },
      getSectionsToRender() {
        const cartItemEl = document.getElementById('main-cart-items');
        if (cartItemEl) {
          const templateId = cartItemEl.closest('.shopify-section').id
                              .replace('cart-items', '')
                              .replace('shopify-section-', '');

          return [
            {
              id: templateId + 'cart-items',
              selector: '#main-cart-items'
            },
            {
              id: templateId + 'cart-footer',
              selector: '#main-cart-footer'
            },
            {
              id: templateId + 'cart-upsell',
              selector: '#main-cart-upsell'
            },
            {
              id: "cart-icon-bubble",
              selector: '#cart-icon-bubble'
            },
            {
              id: 'mobile-cart-icon-bubble',
              selector: '#mobile-cart-icon-bubble'
            }
          ];
        }

        return [
          {
            id: "cart-icon-bubble",
            selector: '#cart-icon-bubble'
          },
          {
            id: 'mobile-cart-icon-bubble',
            selector: '#mobile-cart-icon-bubble'
          },
          {
            id: 'cart-drawer',
            selector: '#CartDrawer'
          }
        ];
      }
    });

    Alpine.data('xModalSearch', (type, desktopMaximunResults, mobileMaximunResults, productTypeSelected) => ({
      t: '',
      result: ``,
      query: '',
      cachedResults: [],
      productTypeSelected: productTypeSelected,
      showSuggest: false,
      loading: false,
      open() {
        this.$refs.open_search.style.opacity = "1";
        this.$refs.open_search.style.maxHeight = "500px";
        this.$refs.open_search.classList.remove("overflow-hidden");
        this.$refs.open_search.classList.remove("hidden");
        this.$refs.input_search.focus();
      },
      close() {
        this.$refs.open_search.style.opacity = "0";
        this.$refs.open_search.style.maxHeight = "0px";
        this.$refs.open_search.classList.add("overflow-hidden");
        this.$refs.open_search.classList.add("hidden");
      },
      keyUp() {
        this.query = this.$el.value;
        return () => {
          clearTimeout(this.t);
          this.t = setTimeout(() => {
            if (this.query != "") {
              this.showSuggest = false;
              this.getSearchResult(this.query);
            } else {
              this.showSuggest = true;
              this.result = "";
            }
          }, 300);
        };
      },
      getSearchResult(query) {
        const limit = window.innerWidth > 767 ? desktopMaximunResults : mobileMaximunResults;
        let q = this.productTypeSelected != productTypeSelected ? `${this.productTypeSelected} AND ${query}` : query;

        const queryKey = q.replace(" ", "-").toLowerCase() + '_' + limit;

        if (this.cachedResults[queryKey]) {
          this.result = this.cachedResults[queryKey];
          return;
        }

        this.loading = true;

        fetch(
          `${Shopify.routes.root}search/suggest` +
            `?q=${encodeURIComponent(q)}` +
            `&${encodeURIComponent('resources[type]')}=${encodeURIComponent(type)}` +
            `&${encodeURIComponent('resources[limit]')}=${encodeURIComponent(limit)}` +
            `&${encodeURIComponent('resources[options][fields]')}=${encodeURIComponent('title,product_type,variants.title,variants.sku,body')}` +
            `&section_id=predictive-search`
        )
          .then((response) => {
            return response.text();
          })
          .then((response) => {
            const parser = new DOMParser();
            const text = parser.parseFromString(response, 'text/html');
            this.result = text.querySelector("#shopify-section-predictive-search").innerHTML;
            this.cachedResults[queryKey] = this.result;
          })
          .finally(() => {
            this.loading = false;
          });
      },
      setProductType(value, input) {
        this.productTypeSelected = value;
        document.getElementById(input).value = value;
        if(this.query != '') {
          this.getSearchResult(this.query);
        }
      },
      focusForm() {
        if (this.$el.value != '') {
          this.showSuggest = false;
        } else {
          this.showSuggest = true;
        }
      }
    }));

    Alpine.store('xHeaderMenu', {
      isSticky: false,
      isTouch: ('ontouchstart' in window) || window.DocumentTouch && window.document instanceof DocumentTouch || window.navigator.maxTouchPoints || window.navigator.msMaxTouchPoints ? true : false,
      sectionId: '',
      stickyType: 'none',
      lastScrollTop: 0,
      themeModeChanged: false,
      showLogoTransparent: true,
      offsetTop: 0,
      clickedHeader: false,
      _touchedEls: new WeakSet(),
      _resizeHandler: null,
      _stickyEl: null,
      _headerContainer: null,
      _stickyContent: null,
      _showMenuTimer: null,
      _hideMenuTimer: null,
      _scrollRafId: null,
      _removeStickyRafId: null,
      _headerResizeRafId: null,
      _useStickyPosition: false,
      _stickyObserver: null,
      _stickySentinel: null,
      _announcementEl: null,
      _stickyAnnouncementEl: null,
      _announcementBeforeHeader: false,
      // Cached header heights — populated by _measureHeader(), consumed on the scroll path.
      _stickyHeight: null,
      _stickyAnnouncementHeight: 0,
      _topHeader: 0,
      selectItem(el, isSub, delayOpen = true) {
        if (this._showMenuTimer) {
          clearTimeout(this._showMenuTimer);
          this._showMenuTimer = null;
        }
        if (this._hideMenuTimer) {
          clearTimeout(this._hideMenuTimer);
          this._hideMenuTimer = null;
        }
        if (!delayOpen) {
          this.showMenu(el, isSub);
          return;
        }
        this._showMenuTimer = setTimeout(() => {
          if (!el.isConnected || (!el.matches(':hover') && !el.contains(document.activeElement))) {
            this._showMenuTimer = null;
            return;
          }
          this.showMenu(el, isSub);
          this._showMenuTimer = null;
        }, 200);
      },
      showMenu(el, isSub) {
        if(el.querySelector(".mega-list-nav")) {
          el.style.setProperty('--mega-menu-height', el.querySelector(".mega-list-nav").offsetHeight + 'px') 
        } 
        el.style.setProperty('--header-container-height', (this._headerContainer || document.getElementById("x-header-container")).offsetHeight + 'px')
        const itemSelector = isSub ? '.toggle-menu-sub' : '.toggle-menu';

        var items = isSub ? el.closest('.toggle-menu').querySelectorAll(itemSelector) : (el.closest('nav') || document).querySelectorAll(itemSelector);

        let toggleMenu = el.querySelector(itemSelector);

        for (var i = 0; i < items.length; i++) {
          if (items[i] === toggleMenu) continue;
          if (isSub) {
            items[i].style.display = "none";
          } else {
            items[i].style.transition = 'none';
            items[i].style.maxHeight = "0px";
            items[i].style.opacity = "0";
            items[i].classList.add('max-h-0', 'overflow-hidden');
          }
        }

        if (toggleMenu) {
          if (isSub) {
            toggleMenu.style.display = 'block';
          } else {
            toggleMenu.style.transition = 'none';
            toggleMenu.style.maxHeight = '';
            toggleMenu.style.opacity = "1";
            toggleMenu.classList.remove('max-h-0', 'overflow-hidden');
          }
        }
      },
      hideMenu(navEl) {
        if (this._showMenuTimer) {
          clearTimeout(this._showMenuTimer);
          this._showMenuTimer = null;
        }
        if (this._hideMenuTimer) clearTimeout(this._hideMenuTimer);
        this._hideMenuTimer = setTimeout(() => {
          var items = (navEl || document).querySelectorAll('.toggle-menu');
          for(var i = 0; i < items.length; i++) {
            items[i].style.transition = '';
            items[i].style.maxHeight = "0px";
            items[i].style.opacity = "0";
            items[i].classList.add("overflow-hidden");
          }
          this._hideMenuTimer = null;
        }, 200);
      },

      // start handle touch menu on the ipad
      touchItem(el, isSub = false) {
        if (this._touchedEls.has(el)) return;
        this._touchedEls.add(el);

        const touchClass = isSub ? 'touched-sub' : 'touched';

        el.addEventListener("touchend", (e) => {
          if (el.classList.contains(touchClass)) {
            window.location.replace(el.getAttribute('href'));
          } else {
            e.preventDefault(); 
            var dropdown = document.querySelectorAll(`.${touchClass}`);
            for(var i = 0; i < dropdown.length; i++) { 
              dropdown[i].classList.remove(touchClass); 
            }

            el.classList.add(touchClass);
            this.selectItem(el.closest('.has-dropdown'), isSub, false);
          }
        });
      },

      // handle sticky header
      initSticky(el, sectionId, stickyType) {
        this.sectionId = sectionId;
        this.stickyType = stickyType;
        this.offsetTop = el.offsetTop;
        this._headerContainer = el;
        this._stickyEl = document.getElementById("sticky-header");
        // Non-transparent headers use CSS `position: sticky` on the section wrapper
        // (no JS spacer, stuck-state detected via an IntersectionObserver sentinel).
        // Transparent headers overlay the hero (absolute, no spacer) and keep the
        // existing `position: fixed` mechanism.
        this._useStickyPosition =
          stickyType !== 'none' && !this._stickyEl.classList.contains('transparent-header');
        this._stickyContent = document.getElementById("sticky-header-content");
        this._announcementEl = document.querySelector(".section-announcement");
        this._stickyAnnouncementEl = document.querySelector("#x-announcement[data-is-sticky='true']");
        this._announcementBeforeHeader =
          document.querySelectorAll(".section-announcement + .section-header").length > 0
          || document.querySelectorAll(".section-announcement + div + .section-header").length > 0;
        if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
        this._resizeHandler = () => {
          if (this._headerResizeRafId) return;
          this._headerResizeRafId = requestAnimationFrame(() => {
            this._headerResizeRafId = null;
            this.reCalculateHeaderHeight();
          });
        };
        window.addEventListener('resize', this._resizeHandler, { passive: true });
        const list = this._stickyEl;
        const announcement = this._stickyAnnouncementEl;
        if (this._headerContainer.dataset.isSticky && announcement) {
          const existingAnnouncement = list.querySelector("#x-announcement");
          if (existingAnnouncement) {
            existingAnnouncement.remove();
          }
          let htmlAnnouncement = this._announcementEl.innerHTML.replaceAll("x-slide-announcement-bar", 'x-slide-announcement-bar-1').replaceAll("section-announcement", 'section-announcement-1');
          if (!this._announcementBeforeHeader) {
            list.insertAdjacentHTML('beforeend', htmlAnnouncement);
          } else {
            list.insertAdjacentHTML('afterbegin', htmlAnnouncement);
          }
          this._announcementEl.style.display = "none";
        }
        // Tear down any sticky observer/sentinel from a previous init. Needed when
        // the theme editor switches sticky type to "none" or to a transparent
        // header — otherwise the old observer keeps driving the sticky state.
        if (this._stickyObserver) { this._stickyObserver.disconnect(); this._stickyObserver = null; }
        if (this._stickySentinel) { this._stickySentinel.remove(); this._stickySentinel = null; }
        if (this._useStickyPosition) {
          // position: sticky keeps the header in flow, so no spacer is needed.
          // Set --top-header up front so the on-scroll-up translate works on the
          // very first scroll (without waiting for the x-intersect.once / a resize).
          this.setTopStickyHeader();
          this.setPositionTop();
          this._initStickyObserver();
        } else {
          this._measureHeader();
          el.style.height = this._stickyHeight + 'px';
          this.setPositionTop();
        }
      },
      _initStickyObserver() {
        if (this._stickyObserver) this._stickyObserver.disconnect();
        const section = document.getElementById(this.sectionId) || this._headerContainer.closest('.shopify-section');
        if (!section || !section.parentElement) return;
        let sentinel = document.getElementById('x-sticky-sentinel');
        if (!sentinel) {
          sentinel = document.createElement('div');
          sentinel.id = 'x-sticky-sentinel';
          sentinel.setAttribute('aria-hidden', 'true');
          // Zero-height marker in normal flow at the header's pre-pinned top.
          sentinel.style.cssText = 'width:100%;height:0;margin:0;padding:0;border:0;pointer-events:none;visibility:hidden;';
          section.parentElement.insertBefore(sentinel, section);
        }
        this._stickySentinel = sentinel;
        // When the sentinel scrolls out of view (above the viewport) the header is
        // pinned → add the sticky styling; when it re-enters → revert.
        this._stickyObserver = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) {
            this.removeStickyHeader(true);
          } else if (entry.boundingClientRect.top < 0) {
            this.addStickyHeader();
          }
        }, { threshold: 0 });
        this._stickyObserver.observe(sentinel);
      },
      setPositionTop() {
        const stickyEl = this._stickyEl;
        const stickyContent = this._stickyContent;
        const announcementEl = this._announcementEl;
        const stickyAnnouncementEl = this._stickyAnnouncementEl;
        // Heights are cached by _measureHeader() on init / resize / theme-mode change
        // and on every sticky pin/unpin, so the scroll path writes only — no reflow.
        if (this._stickyHeight == null) this._measureHeader();

        if (!this._announcementBeforeHeader) {
          const currentTop = (this._headerContainer && this._headerContainer.dataset.isSticky && this.isSticky)
            ? this._stickyHeight
            : 0;
          if (announcementEl) {
            announcementEl.style.top = currentTop + "px";
          }
        } else {
          if (!stickyAnnouncementEl) {
            if (this.isSticky) {
              // Single write based on class check (no reflow)
              stickyEl.style.top = stickyEl.classList.contains('header-up')
                ? "calc(-1 * var(--top-header))"
                : "0px";
            }
          } else if (this._headerContainer && this._headerContainer.dataset.isSticky) {
            stickyContent.style.top = this.isSticky ? "0px" : this._stickyAnnouncementHeight + "px";
            document.documentElement.style.setProperty('--top-header', this._topHeader + "px");
          }
        }
      },
      handleAlwaysSticky() {
        const scrollPos = window.pageYOffset || document.documentElement.scrollTop;
        const stickyLine = document.getElementById(this.sectionId).offsetTop;
        
        if (scrollPos > stickyLine) this.addStickyHeader();
      },
      handelOnScrollSticky() {
        if (this._scrollRafId) return;
        this._scrollRafId = requestAnimationFrame(() => {
          this._scrollRafId = null;
          // Read scroll position before any style writes to avoid a forced reflow.
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const stickyEl = this._stickyEl;
          this.setPositionTop();

          if (scrollTop < this.offsetTop) {
            stickyEl.classList.remove('sticky-header');
          }

          if (Math.abs(scrollTop - this.lastScrollTop) > 10) {
            if (scrollTop < this.lastScrollTop) {
              stickyEl.classList.remove('header-up', 'opacity-0');
              this.setVariableHeightHeader(true);
            } else if (!this.themeModeChanged) {
              stickyEl.classList.add('header-up');
              this.setVariableHeightHeader(false);
            }
            this.lastScrollTop = scrollTop;
          }
          this.themeModeChanged = false;
        });
      },
      addStickyHeader() {
        let isMiniCartOpen = false;
        if (Alpine.store('xMiniCart').open && this.stickyType != 'on-scroll-up') {
          isMiniCartOpen = true;
          Alpine.store('xMiniCart').hideCart();
        }

        requestAnimationFrame(() => {
          let stickyEl = this._stickyEl;
          stickyEl.classList.add("sticky-header", 'reduce-logo-size');

          this.isSticky = true;
          this.showLogoTransparent = false;

          if (this.stickyType == 'on-scroll-up') {
            setTimeout(() => {
              stickyEl.classList.add('on-scroll-up-animation');
            }, 500);
          }
          if (!Alpine.store('xMiniCart').open || window.innerWidth > 768) {
            if (this.stickyType == 'always'
              || this.stickyType == 'reduce-logo-size') stickyEl.classList.add('always-animation');
          }

          if (isMiniCartOpen) {
            Alpine.store('xMiniCart').openCart();
          }

          // The sticky state changes the header height two ways: `reduce-logo-size`
          // (added synchronously above) and the `:class` bindings that drop `lg:pt-5`
          // (header-leibish.liquid) driven by `isSticky`. Alpine flushes
          // those reactive class updates asynchronously, so measure inside nextTick —
          // otherwise the cache keeps the pre-sticky height (~20px too tall on desktop)
          // until the next resize. Still off the scroll path, so frames reuse the cache.
          Alpine.nextTick(() => {
            this.setTopStickyHeader();
            this.setPositionTop();
          });
        });
      },
      removeStickyHeader(force = false) {
        if ((!force && window.pageYOffset > this.offsetTop) || this._removeStickyRafId) return;

        this._removeStickyRafId = requestAnimationFrame(() => {
          this._removeStickyRafId = null;
          if (!force && window.pageYOffset > this.offsetTop) return;

          this.isSticky = false;
          this.showLogoTransparent = true;
          this._stickyEl.classList
            .remove('sticky-header', 'reduce-logo-size', 'always-animation', 'on-scroll-up-animation', 'header-up', 'opacity-0');
          // Mirror addStickyHeader: the non-sticky `:class` bindings (re-adding
          // `lg:pt-5`, dropping `lg:pb-0`) flush asynchronously, so measure after
          // nextTick to avoid caching a height that is ~20px too short.
          Alpine.nextTick(() => {
            this.reCalculateHeaderHeight();
            this.setVariableHeightHeader(false);
            this.setPositionTop();
          });
        });
      },
      handleChangeThemeMode() {
        this.themeModeChanged = true;
        this.reCalculateHeaderHeight();
      },
      reCalculateHeaderHeight() {
        if (this._useStickyPosition) {
          // No spacer in the sticky model — just keep the on-scroll-up translate
          // distance (--top-header) in sync with the current header height.
          this.setTopStickyHeader();
          return;
        }
        this._measureHeader();
        this._headerContainer.style.height = this._stickyHeight + 'px';
      },
      addTransparentHover(el) {
        el.classList.add('sticky-header-active');
      },
      removeTransparentHover(el) {
        if (!(this.isTouch && this.clickedHeader)) {
          el.classList.remove('sticky-header-active');
        }
      },
      setVariableHeightHeader(sticky) {
        if (this._stickyHeight == null) this._measureHeader();
        document.documentElement.style.setProperty(
          '--height-header',
          sticky ? this._stickyHeight + "px" : "0px"
        );
      },
      // Reads all header layout heights in one batch and caches them. Call this only
      // off the scroll path (init / resize / theme-mode change / sticky pin-unpin) so
      // the scroll handlers can position the header from the cache without reflow.
      _measureHeader() {
        const stickyEl = this._stickyEl || document.getElementById("sticky-header");
        if (!stickyEl) return;
        const stickyContent = this._stickyContent || document.getElementById("sticky-header-content");
        const stickyAnnouncementEl = this._stickyAnnouncementEl;
        const stickyInnerAnnouncement = stickyEl.querySelector("#x-announcement");

        this._stickyHeight = stickyEl.offsetHeight;
        this._stickyAnnouncementHeight = stickyAnnouncementEl ? stickyAnnouncementEl.offsetHeight : 0;

        let top_height = stickyContent ? stickyContent.offsetHeight : 0;
        if (stickyAnnouncementEl) {
          top_height += this._stickyAnnouncementHeight + 20;
        }
        if (stickyInnerAnnouncement) {
          top_height += stickyInnerAnnouncement.offsetHeight;
        }
        this._topHeader = top_height;
      },
      setTopStickyHeader() {
        this._measureHeader();
        document.documentElement.style.setProperty('--top-header', this._topHeader + "px");
      }
    });

    Alpine.store('xMobileNav', {
      show: false,
      currentMenuLinks: [],
      open() {
        this.show = true;
        Alpine.store('xPopup').open = true;
      },
      close() {
        this.show = false;
        Alpine.store('xPopup').open = false;
      },
      setActiveLink(linkId) {
        this.currentMenuLinks.push(linkId);
      },
      removeActiveLink(linkId) {
        const index = this.currentMenuLinks.indexOf(linkId);
        if (index !== -1) {
          this.currentMenuLinks.splice(index, 1);
        }
      },
      resetMenu() {
        this.currentMenuLinks = [];
      },
      scrollTop() { 
        document.getElementById('menu-navigation').scrollTop = 0; 
      }
    });

    /* Direct popup state and shared-dialog scroll locks are layered. A dialog can release
       only its own lock without closing a quick view or another popup that opened later. */
    Alpine.store('xPopup', {
      _open: false,
      _scrollLocks: 0,
      get open() {
        return this._open || this._scrollLocks > 0;
      },
      set open(value) {
        this._open = !!value;
      },
      lockScroll() {
        this._scrollLocks += 1;
      },
      unlockScroll() {
        this._scrollLocks = Math.max(0, this._scrollLocks - 1);
      }
    }); 

    Alpine.store('xShowCookieBanner', {
      show: false
    });

    Alpine.store('xMiniCart', {
      open: false,
      type: '',
      loading: false,
      reLoad() {
        this.loading = true;
        const sections = Alpine.store('xCartHelper').getSectionsToRender().map(s => s.id).join(',');
        fetch(
          `${window.location.pathname}?sections=${sections}`
        )
          .then(response => response.json())
          .then(response => {
            Alpine.store('xCartHelper').getSectionsToRender().forEach((section => {
              const sectionElement = document.querySelector(section.selector);
              if (sectionElement && response[section.id]) {
                sectionElement.innerHTML = getSectionInnerHTML(response[section.id], section.selector);
              }
            }));

            this.loading = false;
          });
      },
      openCart() {
        if (window.location.pathname != '/cart') {        
          requestAnimationFrame(() => {
            if (Alpine.store('xQuickView') && Alpine.store('xQuickView').close()) {
              Alpine.store('xQuickView').close();
            }
          });

          requestAnimationFrame(() => {
            document.getElementById('sticky-header').classList.remove('on-scroll-up-animation');

            if (window.innerWidth < 768 || this.type == "drawer") {
              Alpine.store('xPopup').open = true;
            }

            requestAnimationFrame(() => {
              document.getElementById('sticky-header').classList.remove('header-up');
              this.open = true;
            });
            
            if (Alpine.store('xHeaderMenu').stickyType == 'on-scroll-up') {
              setTimeout(() => {
                requestAnimationFrame(() => {
                  document.getElementById('sticky-header').classList.add('on-scroll-up-animation');
                });
              }, 200);
            }
          });
        }
      },
      hideCart() {
        requestAnimationFrame(() => {
          this.open = false;
          Alpine.store('xPopup').open = false;
        });
      }
    });

    Alpine.store('xModal', {
      activeElement: "",
      setActiveElement(element) {
        this.activeElement = element;
      },
      focus(container, elementFocus) {
        Alpine.store('xFocusElement').trapFocus(container, elementFocus);
      },
      removeFocus() {
        const openedBy = document.getElementById(this.activeElement);
        Alpine.store('xFocusElement').removeTrapFocus(openedBy);
      }
    });

    Alpine.store('xFocusElement', {
      focusableElements: ['button, [href], input, select, textarea, [tabindex]:not([tabindex^="-"])'],
      listeners: {},
      trapFocus(container, elementFocus) {
        if ( window.innerWidth < 1025 ) return;

        let c = document.getElementById(container);
        let e = document.getElementById(elementFocus);
        this.listeners = this.listeners || {};
        const elements = Array.from(c.querySelectorAll(this.focusableElements));
        var first = elements[0];
        var last = elements[elements.length - 1];
        
        this.removeTrapFocus();
        
        this.listeners.focusin = (event)=>{
          if (
            event.target !== c &&
            event.target !== last &&
            event.target !== first
          ){
            return;
          }
          document.addEventListener('keydown', this.listeners.keydown);
        };

        this.listeners.focusout = () => {
          document.removeEventListener('keydown', this.listeners.keydown);
        }

        this.listeners.keydown = (e) =>{
          if (e.code.toUpperCase() !== 'TAB') return;
  
          if (e.target === last && !e.shiftKey) {
            e.preventDefault();
            first.focus();
          }
  
          if ((e.target === first || e.target == c) && e.shiftKey) {
            e.preventDefault();
            last.focus();
          }
        }
        document.addEventListener('focusout', this.listeners.focusout);
        document.addEventListener('focusin', this.listeners.focusin);
        e.focus();
      },
      removeTrapFocus(elementToFocus = null) {
        if ( window.innerWidth < 1025 ) return;

        if (this.listeners.focusin) {
          document.removeEventListener('focusin', this.listeners.focusin);
        }
        if (this.listeners.focusout) {
          document.removeEventListener('focusout', this.listeners.focusout);
        }
        if (this.listeners.keydown) {
          document.removeEventListener('keydown', this.listeners.keydown);
        }
        this.listeners = {};
        if (elementToFocus) elementToFocus.focus();
      }
    });

    /* Shared dialog behaviour for snippets/modal-shell.liquid.
       xFocusElement is not reused here because it keys off element ids and bails out
       below 1025px, so mobile modals would get no trap and no focus restore. The Alpine
       Focus plugin is not bundled either, hence the manual trap: a keydown handler wraps
       Tab, and a focusin guard pulls focus back whenever it lands outside the panel —
       which is what recovers focus after it leaves a cross-origin HubSpot form iframe. */
    Alpine.data('xModalShell', (options = {}) => {
      /* State is kept in the closure, not on the returned object: sync() runs inside the
         snippet's x-effect, and reading a reactive flag there would make the effect
         re-run on its own write. */
      let isOpen = false;
      let opener = null;
      let listeners = null;
      let holdsScrollLock = false;
      const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex^="-"])';

      return {
        sync(open) {
          if (!!open === isOpen) return;
          isOpen = !!open;
          isOpen ? this.activate() : this.release();
        },

        panel() {
          return this.$root.querySelector('[data-modal-panel]') || this.$root;
        },

        focusable(panel) {
          return Array.from(panel.querySelectorAll(focusableSelector)).filter(
            (el) => el.getClientRects().length > 0
          );
        },

        focusFirst(panel) {
          (this.focusable(panel)[0] || panel).focus();
        },

        activate() {
          opener = document.activeElement;
          if (options.lockScroll !== false) {
            /* A counted lock cannot overwrite a popup that opens while this dialog is
               active, and it also handles two shared dialogs being open at once. */
            Alpine.store('xPopup').lockScroll();
            holdsScrollLock = true;
          }

          const panel = this.panel();

          listeners = {
            focusin: (event) => {
              if (!isOpen || panel.contains(event.target)) return;
              this.focusFirst(panel);
            },
            keydown: (event) => {
              if (!isOpen || event.key !== 'Tab') return;
              const focusable = this.focusable(panel);
              if (!focusable.length) return;
              const first = focusable[0];
              const last = focusable[focusable.length - 1];

              if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
              } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
              }
            }
          };

          document.addEventListener('focusin', listeners.focusin);
          document.addEventListener('keydown', listeners.keydown);

          /* x-show reveals the overlay in a requestAnimationFrame, so on $nextTick alone the
             panel is still display:none and focus() silently fails. */
          this.$nextTick(() => requestAnimationFrame(() => {
            if (isOpen) this.focusFirst(panel);
          }));
        },

        release() {
          if (listeners) {
            document.removeEventListener('focusin', listeners.focusin);
            document.removeEventListener('keydown', listeners.keydown);
            listeners = null;
          }
          if (holdsScrollLock) {
            Alpine.store('xPopup').unlockScroll();
            holdsScrollLock = false;
          }

          const previous = opener;
          opener = null;
          if (previous && previous.isConnected && typeof previous.focus === 'function') previous.focus();
        },

        destroy() {
          if (!isOpen) return;
          isOpen = false;
          this.release();
        }
      };
    });

    /* ISO 3166-1 alpha-2 codes where the WhatsApp contact option is not offered: the 54 African
       states plus Western Sahara, Réunion, Mayotte and Saint Helena, then the UN Southern Asia
       region (Afghanistan, Bangladesh, Bhutan, India, Iran, Maldives, Nepal, Pakistan, Sri Lanka), and Russia. */
    const WHATSAPP_BLOCKED_COUNTRY_CODES = ['DZ', 'AO', 'BJ', 'BW', 'BF', 'BI', 'CV', 'CM', 'CF', 'TD', 'KM', 'CG', 'CD', 'CI', 'DJ', 'EG', 'GQ', 'ER', 'SZ', 'ET', 'GA', 'GM', 'GH', 'GN', 'GW', 'KE', 'LS', 'LR', 'LY', 'MG', 'MW', 'ML', 'MR', 'MU', 'MA', 'MZ', 'NA', 'NE', 'NG', 'RW', 'ST', 'SN', 'SC', 'SL', 'SO', 'ZA', 'SS', 'SD', 'TZ', 'TG', 'TN', 'UG', 'ZM', 'ZW', 'EH', 'RE', 'YT', 'SH', 'AF', 'BD', 'BT', 'IN', 'IR', 'MV', 'NP', 'PK', 'LK', 'RU'];

    Alpine.data('xHeaderContact', (config = {}) => ({
      contactOpen: false,
      view: 'choices',
      /* Direction of the last view change ('forward' | 'back'); the CSS slides the entering
         view in from that side. Empty on open, which modal-shell already animates. */
      nav: '',
      formReady: false,
      formFailed: false,
      formInstance: '',
      formConfig: null,
      stopFormTracking: null,
      whatsappMessage: '',
      whatsappDefault: config.whatsappDefault || '',
      whatsappAllowed: false,
      regionCheck: null,

      /* Same link the Dondy bubble built: typed text (or the default greeting), then the
         page URL without its query so campaign parameters stay out of the chat. */
      get whatsappHref() {
        const text = (this.whatsappMessage.trim() || this.whatsappDefault)
          + '\n\n' + window.location.origin + window.location.pathname;
        return `https://wa.me/${config.whatsappNumber}?text=${encodeURIComponent(text)}`;
      },

      init() {
        const source = this.$refs.formConfig.content.querySelector('.hs-form-frame, .hs-form-html');
        if (source) this.formConfig = {
          formId: source.dataset.formId,
          portalId: source.dataset.portalId,
          region: source.dataset.region || 'na1'
        };
      },

      openContact() {
        this.view = 'choices';
        this.nav = '';
        this.contactOpen = true;
        this.checkWhatsappRegion();
        if (this.formConfig) window.LeibishHubSpot?.load(this.formConfig.portalId);
      },

      /* [data-contact-drawer] links (Need help) open this drawer. Listening here rather than on
         the link leaves it a plain link wherever no drawer is rendered (a header with no form
         configured). Modified clicks keep the browser's behaviour. */
      openFromLink(event) {
        if (event.ctrlKey || event.metaKey || event.shiftKey || !event.target.closest?.('[data-contact-drawer]')) return;
        event.preventDefault();
        this.openContact();
      },

      /* WhatsApp is not offered to visitors in Africa or Southern Asia. None of those countries
         has a market, so localization.country (the market context, falling back to US) can't
         identify them; the GeoIP country comes from Shopify's browsing_context_suggestions
         endpoint. The option stays hidden until it answers and is shown if the lookup fails. */
      checkWhatsappRegion() {
        if (this.regionCheck || !config.whatsappNumber) return;
        const key = 'leibish:visitor-country';
        let cached = null;
        try { cached = sessionStorage.getItem(key); } catch (e) {}
        this.regionCheck = (cached !== null
          ? Promise.resolve(cached)
          : fetch(`${window.Shopify?.routes?.root || '/'}browsing_context_suggestions.json`)
            .then((response) => response.ok ? response.json() : Promise.reject(response.status))
            .then((data) => {
              const country = data?.detected_values?.country?.handle || '';
              try { sessionStorage.setItem(key, country); } catch (e) {}
              return country;
            })
        ).then(
          (country) => { this.whatsappAllowed = !WHATSAPP_BLOCKED_COUNTRY_CODES.includes(country.toUpperCase()); },
          () => { this.whatsappAllowed = true; }
        );
      },

      /* The view being left is hidden by x-show, so focus has to move explicitly or it
         drops to <body> and escapes the modal-shell trap. x-show only reveals the new view
         in a requestAnimationFrame queued during the effect flush, after $nextTick's
         timeout, so the focus waits for the frame behind it. */
      showView(view, focusTarget) {
        this.nav = view === 'choices' ? 'back' : 'forward';
        this.view = view;
        this.$nextTick(() => requestAnimationFrame(() => focusTarget?.focus()));
      },

      toggleTopic(message) {
        this.whatsappMessage = this.whatsappMessage === message ? '' : message;
      },

      /* Enter sends, as in WhatsApp's desktop app; touch keyboards keep Enter as a newline. */
      sendOnEnter(event, sendLink) {
        if (event.shiftKey || event.isComposing || !window.matchMedia('(pointer: fine)').matches) return;
        event.preventDefault();
        sendLink.click();
      },

      showContactForm(closeButton, target) {
        this.showView('form', closeButton);
        this.$nextTick(() => {
          if (this.formInstance) return;
          /* No configured form or runtime fails at once; trackHsForm handles the rest. */
          const frame = this.formConfig && window.LeibishHubSpot?.mount(target, this.formConfig);
          this.formInstance = frame?.dataset.instanceId || '';
          this.stopFormTracking = window.Eurus.trackHsForm(frame, {
            onReady: () => { this.formReady = true; this.formFailed = false; },
            onFail: () => { this.formReady = this.formFailed = true; }
          });
        });
      },

      destroy() {
        this.stopFormTracking?.();
        if (this.formInstance) window.LeibishHubSpot?.unmount(this.formInstance);
      }
    }));

    /* Ready/fail tracking for a freshly mounted HubSpot frame, driving the
       snippets/hubspot-form-stage.liquid skeleton for xHeaderContact, xHubspotForm, xFormSF
       (form-popup.js) and aiMockupCard (ai-mockup-card.js). Call it in the same task as LeibishHubSpot.mount(), so the listeners are
       in place before HubSpot can pick the frame up.
       The developer embed (snippets/hubspot-forms.liquid) injects a server-rendered form before
       React hydrates it, so the stage drops its skeleton as soon as that markup is in the frame
       and has a height - on-ready only arrives after hydration. Hydration resets the inputs
       (measured: a value typed into the server-rendered form is gone after on-ready), so the
       frame is `inert` from the markup's arrival until on-ready: visible at its final size, but
       nothing can be typed that would be lost. The inert is lifted only by on-ready: if it has
       not come after 10s, onFail shows the email fallback beside the still-inert form, and a
       late on-ready lifts the inert and calls onReady again, which clears the fallback.
       When the embed falls back to the iframe, the frame gets an .hs-form-frame instead of the
       rendered form, and onReady waits for on-ready *and* a height: HubSpot fires on-ready a
       frame or two before it sizes the iframe, and dropping the loading state in between
       collapses the dialog to its padding. There the size wait is capped at 1s, so a frame that
       never sizes still resolves. A ResizeObserver reports the initial size too (no polling),
       and waits out a frame that is hidden when its form arrives.
       onFail runs at once without a frame, or after 10s with neither (an ad blocker on
       hsforms is common); a form that still arrives later calls onReady.
       Returns a function that stops tracking. */
    window.Eurus.trackHsForm = (frame, { onReady, onFail }) => {
      if (!frame) {
        onFail();
        return () => {};
      }
      let sizeObserver = null;
      let sizeTimer;
      let hydrateTimer;
      let rendered = false;
      let hydrateFailed = false;
      let stopped = false;
      const failTimer = setTimeout(onFail, 10000);
      const hydrated = () => {
        clearTimeout(hydrateTimer);
        frame.inert = false;
        contentObserver.disconnect();
        window.removeEventListener('hs-form-event:on-ready', listener);
      };
      const finish = () => {
        clearTimeout(failTimer);
        clearTimeout(sizeTimer);
        sizeObserver?.disconnect();
        if (!stopped && !rendered) onReady();
        rendered = true;
      };
      const watchSize = (cap) => {
        clearTimeout(failTimer);
        if (sizeObserver || rendered) return;
        if (typeof ResizeObserver === 'undefined') return finish();
        sizeObserver = new ResizeObserver((entries) => {
          if (entries[0].contentRect.height > 0) finish();
        });
        if (cap) sizeTimer = setTimeout(finish, cap);
        sizeObserver.observe(frame);
      };
      const contentObserver = new MutationObserver(() => {
        if (!frame.querySelector('[data-hsfc-id="Renderer"]')) return;
        contentObserver.disconnect();
        frame.inert = true;
        hydrateTimer = setTimeout(() => {
          hydrateFailed = true;
          if (!stopped) onFail();
        }, 10000);
        watchSize(0);
      });
      const listener = (event) => {
        if (event.detail?.instanceId !== frame.dataset.instanceId) return;
        hydrated();
        if (hydrateFailed && !stopped) onReady();
        watchSize(1000);
      };
      contentObserver.observe(frame, { childList: true });
      window.addEventListener('hs-form-event:on-ready', listener);
      return () => {
        stopped = true;
        finish();
        hydrated();
      };
    };

    /* Lifecycle glue around window.LeibishHubSpot for the snippets that mount a form
       lazily (on first dialog open, or on x-intersect). Both callers previously inlined
       this in their own x-data with slightly different guards. The instanceId doubles as
       the "already mounted" flag - a separate boolean would just have to be kept in sync
       with it - and the destroy hook is what unregisters a frame that gets torn down,
       including one modal-shell teleported outside the caller's wrapper.
       hsReady/hsFailed drive snippets/hubspot-form-stage.liquid through trackHsForm. */
    Alpine.data('xHubspotForm', (config = {}) => ({
      hsInstanceId: '',
      hsReady: false,
      hsFailed: false,
      hsStopTracking: null,

      mountHsForm(el) {
        if (this.hsInstanceId) return;
        this.hsReady = this.hsFailed = false;
        const frame = window.LeibishHubSpot?.mount(el, config);
        this.hsInstanceId = frame?.getAttribute('data-instance-id') || '';
        this.hsStopTracking = window.Eurus.trackHsForm(frame, {
          onReady: () => { this.hsReady = true; this.hsFailed = false; },
          onFail: () => { this.hsFailed = true; }
        });
      },

      destroy() {
        this.hsStopTracking?.();
        if (this.hsInstanceId) window.LeibishHubSpot?.unmount(this.hsInstanceId);
        this.hsInstanceId = '';
        this.hsReady = this.hsFailed = false;
      }
    }));

    Alpine.data('xTruncateText', () => ({
      truncated: false,
      truncatable: false,
      expanded: false,
      load(truncateEl, skipVisibilityCheck = false) {
        if (!skipVisibilityCheck && (!truncateEl || !truncateEl.getClientRects().length)) return;
        const truncateRect = truncateEl.getBoundingClientRect();
        truncateEl.style.setProperty("--truncate-height", `${truncateRect.height}px`);
      },
      setTruncate(element, skipVisibilityCheck = false) {
        if (!skipVisibilityCheck && (!element || !element.getClientRects().length)) return;
        if (element.offsetHeight < element.scrollHeight || element.offsetWidth < element.scrollWidth) {
          this.truncated = true;
          this.truncatable = true;
          this.expanded = false;
        } else {
          this.truncated = false;
          this.truncatable = false
          this.expanded = true;;
        }
        this.load(element, true);
      },
      open(el) {
        const truncateContainerEl = el.closest('.truncate-container');
        const truncateEl = truncateContainerEl.querySelector('.truncate-text');
        if (truncateEl.classList.contains('truncate-expanded')) {
          this.truncated = true;
          truncateEl.classList.remove('truncate-expanded');
          this.expanded = false;
        } else {
          const truncateInnerEl = truncateEl.querySelector('.truncate-inner');
          window.requestAnimationFrame(() => {
            const truncateInnerRect = truncateInnerEl.getBoundingClientRect();
            truncateEl.style.setProperty("--truncate-height-expanded", `${truncateInnerRect.height}px`);
            truncateEl.classList.add('truncate-expanded');
          });
          this.truncated = false;
          this.expanded = true;
        }
      }
    }));

    Alpine.store('xCartAnalytics', {
      viewCart() {
        fetch(
          '/cart.js'
        ).then(response => {
          return response.text();
        }).then(cart => {
          cart = JSON.parse(cart);
          if (cart.items.length > 0) {
            Shopify.analytics.publish('view_cart', {'cart': cart});
          }
        });
      }
    });

    Alpine.store('xCustomerEvent', {
      fire(eventName, el, data) {
        if (Shopify.designMode) return;
        
        const formatedData = data ? data : xParseJSON(el.getAttribute('x-customer-event-data'));
        Shopify.analytics.publish(eventName, formatedData);
      }
    });

    Alpine.store('xSplide', {
      load(el, configs) {
        const initSlider = () => {
          if (el.dataset.splideInitialized === "true") {
            el.dataset.splideQueued = "false";
            return;
          }

          const id = el.getAttribute("id");
          if (!id) {
            el.dataset.splideQueued = "false";
            return;
          }

          if(configs.classes != undefined) {
            if (!configs.classes.arrow) configs.classes.arrow = "arrow w-8 h-8 p-2 absolute z-10 top-1/2 -translate-y-1/2 hidden md:flex items-center justify-center";
            if (!configs.classes.next) configs.classes.next = "right-0";
            if (!configs.classes.prev) configs.classes.prev = "-rotate-180";
          }
          let splide = new Splide("#" + id, configs);

          if (configs.thumbs) {
            let thumbsRoot = document.getElementById(configs.thumbs);
            let thumbs = thumbsRoot.getElementsByClassName('x-thumbnail');
            let current;
            let _this = this;

            for (let i=0;i<thumbs.length;i++) {
              if (thumbs[i] == current) {
                thumbs[i].classList.remove('opacity-30');
              } else {
                thumbs[i].classList.add('opacity-30');
              }
              thumbs[i].addEventListener('click', function () {
                _this.moveThumbnail(i, thumbs[i], thumbsRoot, configs.thumbs_direction);
                splide.go(i);
              });
            }
            splide.on('mounted move', function () {
              let thumbnail = thumbs[splide.index];
              if (thumbnail) {
                if (current) {
                  current.classList.add('opacity-30');
                }
                thumbnail.classList.remove('opacity-30');
                current = thumbnail;
                _this.moveThumbnail(splide.index, thumbnail, thumbsRoot, configs.thumbs_direction);
              }
            });
          }

          if (configs.hotspot) {
            let hotspotRoot = document.getElementById(configs.hotspot);
            let hotspots = hotspotRoot.getElementsByClassName('x-hotspot');
            let current;

            if (configs.disableHoverOnTouch && (('ontouchstart' in window) || window.DocumentTouch && window.document instanceof DocumentTouch || window.navigator.maxTouchPoints || window.navigator.msMaxTouchPoints)) {
              for (let i=0;i<hotspots.length;i++) {
                hotspots[i].addEventListener('click', function () {
                  splide.go(i);
                });
              }
            } else {
              for (let i=0;i<hotspots.length;i++) {
                hotspots[i].addEventListener('mouseover', function () {
                  splide.go(i);
                });
                hotspots[i].addEventListener('focus', function () {
                  splide.go(i);
                });
              }             
            }
            splide.on('mounted move', function () {
              let hotspot = hotspots[splide.index];
              
              if (hotspot) {
                if (current) {
                  current.classList.remove('active-hotspot');
                }
                hotspot.classList.add('active-hotspot');
                current = hotspot;
              }
            });
          }

          if (configs.progressBar) {
            var bar = splide.root.querySelector( '.splide-progress-bar' );
            splide.on( 'mounted move', function () {
              var end  = splide.Components.Slides.getLength();
              var rate = 100 * (splide.index / end);
              if (bar) {
                var rateBar = rate + Number(bar.style.width.replace("%", ''));
                var maxRate = 100 - Number(bar.style.width.replace("%", ''));
                if(rateBar > 100 ) {
                  rate = maxRate;
                }
                if(document.querySelector('body').classList.contains('rtl')) {
                  bar.style.marginRight = rate + '%';
                }else {
                  bar.style.marginLeft = rate + '%';
                }  
              }
            });
          }
          if(splide.root.querySelector('.card-product')) {
            splide.on('resized', function() { 
              var maxHeight = 0;
              splide.Components.Slides.get().forEach((item) => {
                if (item.slide.offsetHeight > maxHeight) {
                   maxHeight = item.slide.offsetHeight;
                }
              });
              splide.Components.Slides.get().forEach((item) => {
                item.slide.style.height = maxHeight+"px";
              });
            })
          }

          if (configs.events) {
            configs.events.forEach((e) => {
              splide.on(e.event, e.callback);
            });
          }

          
          el.splide = splide;
          splide.mount();
          el.dataset.splideInitialized = "true";
          el.dataset.splideQueued = "false";
        }

        const initSliderWithAssets = () => {
          if (el.dataset.splideInitialized === "true" || el.dataset.splideQueued === "true") return;
          el.dataset.splideQueued = "true";

          if (!window.Eurus.loadedScript.has('slider')) {
            deferScriptLoad('slider', window.Eurus.sliderScript, initSlider, true);
          } else if (window.Splide){
            initSlider();
          } else {
            document.addEventListener('slider loaded', initSlider, { once: true });
          }
        };

        initSliderWithAssets();
      },
      moveThumbnail(index, thumbnail, thumbsRoot, direction) {
        if (direction == 'vertical') {
          setTimeout(() => {
            thumbsRoot.scrollTop = (index+1) * thumbnail.offsetHeight - thumbsRoot.offsetHeight*0.5 + thumbnail.offsetHeight*0.5 + index*12;
          },50);
        } else {
          thumbsRoot.scrollLeft = (index-2) * thumbnail.offsetWidth;
        }
      }
    });

    Alpine.data('xParallax', () => ({
      debounce(func, wait) {
        var timeout;
        return function() {
            var context = this, args = arguments;
            var later = function() {
              timeout = null;
              func.apply(context, args);
            };
          clearTimeout(timeout);
          timeout = setTimeout(later, wait);
        };
      },
      load(disable) {
        if (disable) return;

        if ("IntersectionObserver" in window && 'IntersectionObserverEntry' in window) {
          const observerOptions = {
            root: null,
            rootMargin: '0px 0px',
            threshold: 0
          };

          var observer = new IntersectionObserver(handleIntersect, observerOptions);
          var el;
          function handleIntersect(entries) {
            entries.forEach(function(entry) {
              if (entry.isIntersecting) {
                el = entry.target;
                window.addEventListener('scroll', parallax, {passive: true, capture: false});
              } else {
                window.removeEventListener('scroll', parallax, {passive: true, capture: false});
              }
            });
          }

          observer.observe(this.$el);
          
          var parallax = this.debounce(function() {
            var rect = el.getBoundingClientRect();
            var speed = (window.innerHeight / el.parentElement.offsetHeight) * 20;
            var shiftDistance = (rect.top - window.innerHeight) / speed;
            var maxShiftDistance = el.parentElement.offsetHeight / 11;
            
            if (shiftDistance < -maxShiftDistance || shiftDistance > maxShiftDistance) {
              shiftDistance = -maxShiftDistance;
            }
            
            el.style.transform = 'translate3d(0, '+ shiftDistance +'px, 0)';
          }, 10);
        }
      }
    }));

    /* Inline engraving control (snippets/engraving-inline.liquid): ring PDP, DYO setting /
       final steps and quick view. It lives here, not in a per-page script, so it works
       wherever the control renders, quick view included. One entry per product handle: the
       text is kept for this browser session only, so an engraving typed on one ring never
       pre-fills (and silently orders on) another; the font defaults to the last one chosen
       anywhere. The DYO copy in dyo_data is read and written only for the setting this
       product put into the flow. handleDyoButton reads order(handle). */
    Alpine.store('engravingPopupState', {
      maxLength: 20,
      defaultFontId: 'romantic-script',
      // Labels are translated in Liquid; every control passes them in (setFontLabels).
      fonts: [
        { id: 'classic-serif', label: '', family: "'Times New Roman', Times, serif" },
        { id: 'romantic-script', label: '', family: "'Monotype Corsiva', 'Snell Roundhand', 'Brush Script MT', cursive" },
        { id: 'modern-block', label: '', family: "'Arial', 'Helvetica Neue', Helvetica, sans-serif" },
      ],
      entries: {},

      setFontLabels(labels = {}) {
        this.fonts.forEach((font) => { font.label = labels[font.id] || font.label; });
      },

      font(id) {
        return this.fonts.find((font) => font.id === id) || this.fonts[0];
      },

      sessionKey(handle) {
        return `leibish:engraving:${handle}`;
      },

      // Storage can throw (blocked site data, quota): a failure only loses the saved
      // value, never the control. A blank value removes the key.
      readStorage(area, key) {
        try {
          return window[area].getItem(key) || '';
        } catch (error) {
          return '';
        }
      },

      writeStorage(area, key, value) {
        try {
          if (value) {
            window[area].setItem(key, value);
          } else {
            window[area].removeItem(key);
          }
        } catch (error) {
        }
      },

      // The DYO setting only when this product put it into the flow (handleDyoButton
      // stores product.handle), so a stale flow for another ring neither pre-fills nor
      // receives this text.
      dyoSetting(handle) {
        try {
          const dyoData = JSON.parse(localStorage.getItem('dyo_data') || 'null');
          const setting = dyoData?.process?.setting;
          return { dyoData, setting: handle && setting?.productHandle === handle ? setting : null };
        } catch (error) {
          return { dyoData: null, setting: null };
        }
      },

      // Created by the product's first control: this session's text, else its DYO text;
      // its DYO font, else the last font chosen anywhere. The label match only serves
      // flows saved before handleDyoButton stored the font id.
      entry(handle) {
        if (!this.entries[handle]) {
          // Retired site-wide key: it carried one product's engraving onto every other.
          this.writeStorage('localStorage', 'engravingText', '');
          const setting = this.dyoSetting(handle).setting || {};
          const fontId = setting.engravingFontId
            || this.fonts.find((font) => font.label && font.label === setting.engravingFont)?.id
            || this.readStorage('localStorage', 'engravingFontId');
          this.entries[handle] = {
            text: (this.readStorage('sessionStorage', this.sessionKey(handle)) || setting.engravingText || '').slice(0, this.maxLength),
            fontId: this.fonts.some((font) => font.id === fontId) ? fontId : this.defaultFontId,
          };
        }
        return this.entries[handle];
      },

      // What is ordered: the trimmed text, and a font only with text. Blank for a
      // product with no control on the page.
      order(handle) {
        const entry = this.entries[handle];
        const text = entry ? entry.text.trim() : '';
        return { text, font: text ? this.font(entry.fontId).label : '', fontId: entry ? entry.fontId : '' };
      },

      save(handle) {
        const entry = this.entries[handle];
        if (!entry) return;
        const order = this.order(handle);
        this.writeStorage('sessionStorage', this.sessionKey(handle), order.text ? entry.text : '');
        this.writeStorage('localStorage', 'engravingFontId', entry.fontId);

        const { dyoData, setting } = this.dyoSetting(handle);
        if (!setting) return;
        Object.assign(setting, { engravingText: order.text, engravingFont: order.font, engravingFontId: order.fontId });
        this.writeStorage('localStorage', 'dyo_data', JSON.stringify(dyoData));
        if (Alpine.store('xDYO')) {
          Alpine.store('xDYO').dyoData = dyoData;
        }
      },
    });

    // One instance per rendered control, bound to its product's entry. `embedded` = inside
    // an accordion row that already collapses it (the RTR DYO rtr-acc), so it stays open.
    Alpine.data('xEngravingInline', ({ handle = '', embedded = false, fontLabels = {} } = {}) => {
      const eng = Alpine.store('engravingPopupState');
      eng.setFontLabels(fontLabels);

      return {
        eng,
        handle,
        entry: eng.entry(handle),
        open: embedded,
        previewId: null,
        previewTimer: null,

        // The accordion row's summary sits outside the component; mirror the text into
        // it from whichever control changes the entry.
        init() {
          this.syncDisplay();
          this.$watch('entry.text', () => this.syncDisplay());
        },

        get order() {
          return this.eng.order(this.handle);
        },

        get hasEngraving() {
          return this.order.text !== '';
        },

        get font() {
          return this.eng.font(this.entry.fontId);
        },

        // The font the field and caption are drawn in: a hovered chip's while the mouse
        // rests on it, otherwise the selected one. Only a click changes what is ordered.
        get shownFont() {
          return this.eng.fonts.find((font) => font.id === this.previewId) || this.font;
        },

        syncDisplay() {
          const slot = this.$el.closest('.rtr-acc')?.querySelector('[data-rtr-engraving-display]');
          if (slot) slot.textContent = this.order.text;
        },

        onInput() {
          if (this.entry.text.length > this.eng.maxLength) {
            this.entry.text = this.entry.text.slice(0, this.eng.maxLength);
          }
          this.eng.save(this.handle);
        },

        // Mouse only: touch has no hover, and a tap selects straight away. The short
        // delay stops the field flickering through every font when the pointer crosses
        // the chips on its way to Add to Cart below them.
        previewFont(event, id) {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(this.previewTimer);
          this.previewTimer = setTimeout(() => { this.previewId = id; }, 80);
        },

        // On leaving the chip row, not each chip, so the gap between two chips does not
        // flash the selected font back.
        endPreview() {
          clearTimeout(this.previewTimer);
          this.previewId = null;
        },

        selectFont(id) {
          this.endPreview();
          this.entry.fontId = id;
          this.eng.save(this.handle);
        },

        toggle() {
          this.open = !this.open;
          // .is-open makes the panel visible as soon as Alpine patches the class.
          if (this.open) this.$nextTick(() => this.$refs.input?.focus());
        },

        // Esc closes the innermost layer only: while the panel is open it does not
        // reach quick view's own Esc handler.
        close(event) {
          if (embedded || !this.open) return;
          event?.stopPropagation();
          this.open = false;
          this.$refs.head?.focus();
        },

        clear() {
          this.entry.text = '';
          this.eng.save(this.handle);
          this.$refs.input?.focus();
        },

        // Radiogroup keyboard contract: arrows move and select, wrapping; Left/Right
        // follow the reading direction.
        onFontKey(event, index) {
          const steps = { ArrowDown: 1, ArrowUp: -1, ArrowRight: 1, ArrowLeft: -1 };
          let step = steps[event.key];
          if (!step) return;
          event.preventDefault();
          const horizontal = event.key === 'ArrowRight' || event.key === 'ArrowLeft';
          if (horizontal && getComputedStyle(event.currentTarget).direction === 'rtl') {
            step = -step;
          }
          const fonts = this.eng.fonts;
          const next = (index + step + fonts.length) % fonts.length;
          this.selectFont(fonts[next].id);
          this.$refs.fonts.querySelectorAll('[role="radio"]')[next]?.focus();
        },
      };
    });

    Alpine.store('xDYO', {
      dyoData: localStorage.getItem('dyo_data') ? JSON.parse(localStorage.getItem('dyo_data')) : null,
      getLocaleRootPath() {
        const root = window.Shopify?.routes?.root || '/';
        const rootPath = root.replace(window.location.origin, '').split('?')[0].replace(/\/$/, '');
        return rootPath === '/' ? '' : rootPath;
      },
      buildStorefrontPath(path) {
        if (typeof window.DYObuildStorefrontPath === 'function') {
          return window.DYObuildStorefrontPath(path);
        }

        const localeRootPath = this.getLocaleRootPath();
        const parsedUrl = new URL(path || '/', window.location.origin);

        if (parsedUrl.origin !== window.location.origin) {
          return parsedUrl.toString();
        }

        let storefrontPath = parsedUrl.pathname || '/';
        if (localeRootPath && storefrontPath !== localeRootPath && storefrontPath.indexOf(`${localeRootPath}/`) !== 0) {
          storefrontPath = `${localeRootPath}${storefrontPath}`;
        }

        return `${storefrontPath.replace(/\/{2,}/g, '/')}${parsedUrl.search}${parsedUrl.hash}`;
      },
      setDyoData(key, value) {
        let dyoData = localStorage.getItem('dyo_data') ? JSON.parse(localStorage.getItem('dyo_data')) : {};
        dyoData[key] = value;
        this.dyoData = dyoData;
        localStorage.setItem('dyo_data', JSON.stringify(dyoData));
      },
      updateProcess(process, key, value) {
        this.updateProcessFields(process, { [key]: value });
      },
      updateProcessFields(process, values) {
        let dyoData = JSON.parse(localStorage.getItem('dyo_data'));
        Object.assign(dyoData.process[process], values);
        this.dyoData = dyoData;
        localStorage.setItem('dyo_data', JSON.stringify(dyoData));
      },
      currentCollection: '',
      load() {
        if (!this.inProcess()) {
          return;
        }

        const activeHeader = this.getActiveHeader();
        if (
          this?.dyoData?.process[activeHeader]?.position == '2'
          && !this?.dyoData?.process[activeHeader]?.urlCollection
        ) {
          // When see step 2 without completing step 1
          window.location.replace(this?.dyoData?.general?.designUrl ?? '/');
          return;
        }
      },
      // Mirrors the sticky machinery in assets/header.js (the LEIBISH header) so both
      // headers publish --height-header the same way: it holds the height the header
      // currently covers at the top of the viewport, and 0 while it is scrolled away.
      // rtr-dyo.css offsets every sticky DYO pane from that variable.
      stickyHeader(element, stickyType = 'on-scroll-up') {
        if (!this.inProcess() || !element) {
          return;
        }

        const headerParent = element?.closest('.section-header');
        if (!headerParent) {
          return;
        }

        // Desktop stays pinned; mobile follows the setting and its schema default.
        const stickyMode = stickyType || 'on-scroll-up';
        const desktopQuery = window.matchMedia('(min-width: 1024px)');
        let onScrollUp = !desktopQuery.matches && stickyMode === 'on-scroll-up';

        // x-init runs again whenever the theme editor re-renders the section; without
        // this the window listeners below would stack up on every re-render.
        if (element.dataset.dyoStickyInit) {
          return;
        }
        element.dataset.dyoStickyInit = '1';

        headerParent.classList.add('dyo-sticky-header');

        let headerHeight = 0;
        let offsetTop = 0;
        let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        let heightHeaderValue = null;
        let scrollRaf = null;
        let resizeRaf = null;
        let animationTimer = null;
        const eleFilter = document.querySelector('#header-filter');

        const setHeaderHeight = (visible) => {
          const height = visible && (desktopQuery.matches || stickyMode !== 'none') ? headerHeight : 0;
          // Scrolling crosses the 10px threshold on nearly every frame, so skip the
          // style writes (and the recalc they cost) when the value has not changed.
          if (heightHeaderValue === height) return;
          heightHeaderValue = height;
          document.documentElement.style.setProperty('--height-header', `${height}px`);
          if (eleFilter) {
            eleFilter.style.top = `${height}px`;
          }
        };

        const measure = () => {
          onScrollUp = !desktopQuery.matches && stickyMode === 'on-scroll-up';
          if (!onScrollUp) {
            clearTimeout(animationTimer);
            element.classList.remove('dyo-sticky-active', 'on-scroll-up-animation', 'header-up', 'opacity-0');
          }
          headerHeight = element.offsetHeight;
          offsetTop = headerParent.offsetTop;
          headerParent.style.setProperty('--dyo-header-height', `${headerHeight}px`);
          setHeaderHeight(!onScrollUp || !element.classList.contains('header-up'));
        };

        const update = () => {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

          if (scrollTop <= offsetTop) {
            // Back at the top: the header sits in the flow and is fully visible, so it
            // still covers its own height — keep it reserved instead of zeroing it.
            clearTimeout(animationTimer);
            element.classList.remove('dyo-sticky-active', 'on-scroll-up-animation', 'header-up', 'opacity-0');
            setHeaderHeight(true);
            lastScrollTop = scrollTop;
            return;
          }

          if (!element.classList.contains('dyo-sticky-active')) {
            element.classList.add('dyo-sticky-active');
            clearTimeout(animationTimer);
            // Delay the transition class so the first pin does not animate.
            animationTimer = setTimeout(() => {
              element.classList.add('on-scroll-up-animation');
            }, 500);
          }

          if (Math.abs(scrollTop - lastScrollTop) > 10) {
            if (scrollTop < lastScrollTop) {
              element.classList.remove('header-up', 'opacity-0');
              setHeaderHeight(true);
            } else {
              element.classList.add('header-up');
              setHeaderHeight(false);
            }
            lastScrollTop = scrollTop;
          }
        };

        const onScroll = () => {
          if (scrollRaf || !onScrollUp) return;

          scrollRaf = requestAnimationFrame(() => {
            scrollRaf = null;
            update();
          });
        };

        const onResize = () => {
          if (resizeRaf) return;

          resizeRaf = requestAnimationFrame(() => {
            resizeRaf = null;
            measure();
            if (onScrollUp) update();
          });
        };

        measure();
        // The page can load already scrolled (restored position, #anchor).
        if (onScrollUp) {
          update();
        }
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize, { passive: true });

        if (document.fonts?.ready) {
          document.fonts.ready.then(() => requestAnimationFrame(measure));
        }
      },
      inProcess() {
        if (window.DYO_in_process) {
          return window.DYO_in_process;
        }

        if (window.location.hash === '#dyo') {
          window.DYO_in_process = window.location.hash;
          return window.DYO_in_process;
        }

        return '';
      },
      getActiveHeader() {
        if (!this.currentCollection) {
          return 'confirm';
        }
        
        if (window.DYOcollections.hasOwnProperty(this.currentCollection)) {
          return 'setting';
        }

        return 'stone'; 
      },
      autoSelectStoneSize(inputs) {
        if (this.dyoData?.process?.stone?.metafields?.weight) {
          requestAnimationFrame(() => {
            const { result } = this.checkOptions(inputs, this.dyoData.process.stone.metafields.weight);
            if (result) {
              result.checked = true;
              result.dispatchEvent(new Event('change'));
            }
          });
        }
      },
      updateVariantSetting(stoneWeight) {
        if (
          this.dyoData?.process?.setting?.productVariantId
          && Array.isArray(this.dyoData?.process?.setting?.variants)
        ) {
          let variantId = this.dyoData.process.setting.productVariantId;
          const variants = JSON.parse(JSON.stringify(this.dyoData.process.setting.variants));
          const currentVariant = variants.find(variant => variant.id == variantId);
          const currentVariantOptionData = window.DYOanalysisOptions(currentVariant?.options ?? []);

          const { result } = this.checkOptions(variants, stoneWeight, currentVariantOptionData.otherOptions);

          this.updateProcess(
            'setting',
            'productVariantId',
            result?.id
          );
        }
      },
      handleDyoButton(process, productImg = '', productHandle = '',  productVariantId = '', productMetafields = '', productVariants = '') {
        // Get Ring Size from the dropdown
        const ringSize = document.querySelector('#ring-size')?.value;
        // Blank unless this product's engraving control is on the page.
        const engraving = Alpine.store('engravingPopupState')?.order(productHandle) || {};
        const engravingText = engraving.text || '';
        const engravingFont = engraving.font || '';
        const engravingFontId = engraving.fontId || '';
        const rtrSelections = typeof window.DYORTRcollectSelections === 'function'
          ? window.DYORTRcollectSelections()
          : {};

        this.updateProcessFields(process, {
          urlProduct: this.buildStorefrontPath(`/products/${productHandle}?variant=${productVariantId}#dyo`),
          img: productImg,
          productHandle,
          productVariantId,
          variants: productVariants,
          metafields: productMetafields,
          ringSize,
          engravingText,
          engravingFont,
          engravingFontId,
          rtrSelections
        });
      },
      checkOptions(items, stoneWeight, currentOtherOptions = []) {
        let result;
        let min = 999999, max = 0, isValid = false;
        const weight = parseFloat(stoneWeight);

        items.forEach((item) => {
          const optionData = window.DYOanalysisOptions(item?.options ?? [item?.value]);

          if (
            !isValid
            && JSON.stringify(optionData?.otherOptions) === JSON.stringify(currentOtherOptions)
          ) {
            const from = parseFloat(optionData.from);
            const to = parseFloat(optionData.to);

            if (from <= weight && weight <= to) {
              isValid = true;
              result = item;
            } else {
              if (weight < from && from < min) { // If stone weight smaller than all options => Choose this variant
                min = from;
                result = item;
              }
              if (weight > to && from > max) { // If stone weight bigger than all options => Choose this variant
                max = to;
                result = item;
              }
            }
          }
        });

        return { result, isValid };
      },
      analysisOptions(options) { // Suggest use window.analysisOptions(options)
        let otherOptions = [], from = 0, to = 0;

        options.forEach(option => { // ["0.15 - 0.49Ct", "Metal", "18k Gold", ...];
          const match = option.match(/(\d+(\.\d+)?)\s*-\s*(\d+(\.\d+)?)Ct/);
          if (match) {
            from = match[1];
            to = match[3];
            return;
          }

          const singleCaratMatch = option.match(/(\d+(\.\d+)?)\s*(Ct|Carat)/i);
          if (singleCaratMatch) {
            from = singleCaratMatch[1];
            to = singleCaratMatch[1];
          } else {
            otherOptions.push(option);
          }
        });

        return {
          from: from,                 // 0.15
          to: to,                     // 0.49
          otherOptions: otherOptions  // ["Metal", "18k Gold", ...]
        };
      },
      // Stones and settings share one Shape filter on custom.stone_shape, whose values are the
      // shape metaobject ids (`filter.shapeIds`). The shape text no longer filters anything;
      // a process saved before the ids existed opens the collection without a shape filter.
      getCollectionFilterUrl(dyoType, handleRedirect, filter) {
        const filterName = 'filter.v.t.custom.stone_shape';
        let hash = '';
        let param = '';

        if (filter?.weight) {
          hash = `#dyo-filter-center-stone-weight=${filter.weight}`;
        }
        if (filter?.centerStoneWeight) {
          const centerStoneWeight = filter.centerStoneWeight.replace(/\s+/g, '');
          hash = `#dyo-filter-weight=${centerStoneWeight}`;
        }

        const shapeIds = Array.isArray(filter?.shapeIds) ? filter.shapeIds.filter(Boolean) : [];
        param = shapeIds.map(id => `${filterName}=${encodeURIComponent(id)}`).join('&');

        return this.buildStorefrontPath(`/collections/${handleRedirect}?${param}${hash}`);
      },
      // Builds the stone-collection URL from the live DYO selector choices
      // (stone color, shape, carat). The chosen stone color decides whether the
      // shopper lands on the diamond or the gemstone collection. Falls back to
      // the setting product's metafields when a selector is absent.
      // The stone filters (custom.stone_shape, stone_color, gemstone_type,
      // carat_range) take metaobject entry ids, so the English selector values
      // are mapped by entry handle through the ids that dyo-stone-filter-ids
      // renders; a value without an entry adds no filter instead of a dead param.
      getDyoStoneFilterIds() {
        if (this._dyoStoneFilterIds) return this._dyoStoneFilterIds;
        try {
          this._dyoStoneFilterIds = JSON.parse(document.getElementById('dyo-stone-filter-ids')?.textContent || '{}');
        } catch (error) {
          this._dyoStoneFilterIds = {};
        }
        return this._dyoStoneFilterIds;
      },
      getDyoSelectorsFilterUrl(fallback) {
        const gemColors = ['Emerald', 'Sapphire', 'Ruby', 'Colored Sapphires', 'Aquamarine', 'All Gemstones'];
        const mainColorOverrides = {
          'Champagne Diamond': 'Brown', // champagne stones are filed under Brown in custom.stone_color
          'Argyle Pink Diamond': 'Pink',
          'Fancy White Diamond': 'White'
        };

        const stoneColor = document.querySelector('input[data-rtr-stone-color]:checked')?.value ?? '';
        const carat = document.querySelector('input[name="properties[RTR Carat Size]"]:checked')?.value
          || fallback?.centerStoneWeight;

        const isGemstone = gemColors.includes(stoneColor);
        const handleRedirect = isGemstone
          ? this.dyoData?.general?.collections?.stone
          : this.dyoData?.general?.collections?.diamond;

        const ids = this.getDyoStoneFilterIds();
        const handleize = (value) => String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const params = [];
        const addParam = (name, id) => {
          if (id) params.push(`${name}=${encodeURIComponent(id)}`);
        };

        // A shape without its own entry falls back to its family (Wide Cushion is
        // listed under Cushion). With no shape selector, the setting's own shape ids.
        const shapeAliases = { 'wide-cushion': 'cushion' };
        const selectedShape = document.querySelector('input[name="properties[RTR Stone Shape]"]:checked')?.value;
        if (selectedShape) {
          const shapeHandle = handleize(selectedShape);
          addParam('filter.v.t.custom.stone_shape', ids.shape?.[shapeHandle] ?? ids.shape?.[shapeAliases[shapeHandle]]);
        } else if (Array.isArray(fallback?.shapeIds)) {
          fallback.shapeIds.forEach((id) => addParam('filter.v.t.custom.stone_shape', id));
        }

        if (isGemstone) {
          if (stoneColor === 'Colored Sapphires') {
            // Same colours as the collection's Colored Sapphire group (filter-shortcut-ids).
            addParam('filter.v.t.custom.gemstone_type', ids.gem?.sapphire);
            ['green', 'orange', 'peach', 'pink', 'purple', 'violet', 'yellow']
              .forEach((color) => addParam('filter.v.t.custom.stone_color', ids.color?.[color]));
          } else if (stoneColor && stoneColor !== 'All Gemstones') {
            addParam('filter.v.t.custom.gemstone_type', ids.gem?.[handleize(stoneColor)]);
          }
        } else if (stoneColor === 'Chameleon Diamond') {
          // Same rule as /collections/chameleon-diamonds: chameleon stones keep their
          // Green/Yellow/Brown in custom.stone_color and are flagged by this metafield.
          addParam('filter.p.m.diamond.chameleon', '1');
        } else if (stoneColor) {
          const mainColor = mainColorOverrides[stoneColor] ?? stoneColor.replace(/\s*Diamond$/i, '');
          const colorHandle = handleize(mainColor) === 'grey' ? 'gray' : handleize(mainColor);
          addParam('filter.v.t.custom.stone_color', ids.color?.[colorHandle]);
        }

        // The stone collections filter weight through the custom.carat_range
        // facet, whose values are fixed buckets. The chosen carat (a single
        // value like "1.00 Ct" or a range like "0.90 - 1.15") maps onto every
        // bucket it touches; a value sitting exactly on a bucket boundary
        // matches both neighbours so the stone can't be filtered out.
        if (carat) {
          const weightBuckets = [
            { from: 0.01, to: 0.50, label: '0.01 - 0.50 Ct', handle: '0-01-0-50-ct' },
            { from: 0.50, to: 1.00, label: '0.50 - 1.00 Ct', handle: '0-50-1-00-ct' },
            { from: 1.00, to: 2.00, label: '1.00 - 2.00 Ct', handle: '1-00-2-00-ct' },
            { from: 2.00, to: 5.00, label: '2.00 - 5.00 Ct', handle: '2-00-5-00-ct' },
            { from: 5.00, to: Infinity, label: '5.00+ Ct', handle: '5-00-ct-plus' }
          ];
          const selectedBucket = weightBuckets.find((bucket) => bucket.label === carat.trim());
          if (selectedBucket) {
            addParam('filter.p.m.custom.carat_range', ids.carat?.[selectedBucket.handle]);
          } else {
            const numbers = (carat.match(/\d+(?:\.\d+)?/g) ?? []).map(Number);
            if (numbers.length) {
              const from = Math.min(...numbers);
              const to = Math.max(...numbers);
              weightBuckets
                .filter((bucket) => bucket.from <= to && bucket.to >= from)
                .forEach((bucket) => addParam('filter.p.m.custom.carat_range', ids.carat?.[bucket.handle]));
            }
          }
        }

        return this.buildStorefrontPath(`/collections/${handleRedirect}?${params.join('&')}`);
      },
      redirectConfirmPage() {
        const settingHandle = this.dyoData?.process?.setting?.productHandle;
        const settingVariantId = this.dyoData?.process?.setting?.productVariantId;
        const stoneHandle = this.dyoData?.process?.stone?.productHandle;
        // const stoneVariantId = this.dyoData?.process?.stone?.productVariantId;

        if (!settingHandle || !settingVariantId || ! stoneHandle) {
          window.location.href = this.dyoData?.process?.setting?.urlCollection
          || this.dyoData?.process?.stone?.urlCollection
          || this.dyoData?.general?.designUrl
          || '/';
          return;
        }

        const handleConfirmPage = 'confirmation-dyo';
        
        window.location.href = `${window.Shopify.routes.root}pages/${handleConfirmPage}`;
      },
      isMissMatch() {
        const normalizeDyoValue = (value) => String(value || '')
          .trim()
          .toLowerCase()
          .replace(/&amp;/g, '&')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');

        // Check localstorage data
        if (
          !this.dyoData?.process?.setting?.productVariantId
          || !Array.isArray(this.dyoData?.process?.setting?.variants)
          || !Array.isArray(this.dyoData?.process?.setting?.metafields?.shape)
          || !this.dyoData?.process?.stone?.metafields?.weight
          || !this.dyoData?.process?.stone?.metafields?.shape
        ) {
          return true;
        }

        // Check Stone Shape in Setting Shape
        const rtrStoneShape = this.dyoData.process.setting.rtrSelections?.['RTR Stone Shape'];
        const settingShapes = rtrStoneShape
          ? [rtrStoneShape]
          : this.dyoData.process.setting.metafields.shape;
        const stoneShape = normalizeDyoValue(this.dyoData.process.stone.metafields.shape);
        const inSettingShape = settingShapes.some((shape) => normalizeDyoValue(shape) === stoneShape);
        if (!inSettingShape) {
          return true;
        }

        // Check Stone Weight in options 'Center Stone Size'
        let variantId = this.dyoData.process.setting.productVariantId;
        const variants = JSON.parse(JSON.stringify(this.dyoData.process.setting.variants));
        const currentVariant = variants.find(variant => variant.id == variantId);
        const currentVariantOptionData = window.DYOanalysisOptions(currentVariant?.options ?? []);
        const { result, isValid } = this.checkOptions(variants, this.dyoData.process.stone.metafields.weight, currentVariantOptionData.otherOptions);
        if (!isValid && result?.id != variantId) {
          return true;
        }

        return false;
      }
    });
};

// Register Alpine components - handle both cases:
// 1. If Alpine hasn't initialized yet, wait for the event
// 2. If Alpine is already initialized, register immediately
if (window.Alpine && window.Alpine.version) {
  registerAlpineComponents();
} else {
  document.addEventListener('alpine:init', registerAlpineComponents);
}
